import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FiChevronRight,
  FiX,
  FiMenu,
  FiArrowRight,
  FiCheck,
  FiStar,
  FiZap,
  FiCpu,
  FiTruck,
  FiPackage,
  FiBarChart2,
  FiMessageCircle,
  FiDollarSign,
  FiUsers,
  FiGrid,
  FiClock,
  FiShoppingCart,
  FiCreditCard,
  FiDatabase,
  FiShield,
  FiBriefcase,
  FiLock,
  FiBell,
  FiUserCheck,
  FiPercent,
  FiLayers,
  FiClipboard,
  FiPlay,
  FiPause,
  FiChevronLeft,
  FiCoffee,
} from 'react-icons/fi';
import { getAdminUrl } from 'shared/config';

const BP = 768;
const COL = {
  pri: '#0a0e27',
  pri2: '#2952CC',
  acc: '#6382eb',
  acc2: '#4f6ef2',
  bg: '#f7f8fc',
  white: '#FFFFFF',
  text: '#202124',
  muted: '#5e6570',
  light: '#f0f1f5',
  border: '#e8eaef',
  neutral: '#e5e7eb',
  indigoBg: '#eef2ff',
  green: '#10b981',
  greenDark: '#059669',
  greenDeep: '#064e3b',
  greenDeep2: '#065f46',
  greenBorder: '#a7f3d0',
  greenBg: '#ecfdf5',
  greenBg2: '#f0fdf4',
  red: '#ea4335',
  redBg: '#fef2f2',
  amber: '#f59e0b',
  amberDeep: '#92400e',
  amberBorder: '#fde68a',
  amberLight: '#fef3c7',
  amberBg: '#fffbeb',
  purple: '#8b5cf6',
  purpleDeep: '#6d28d9',
  purpleBorder: '#ddd6fe',
  purpleLight: '#ede9fe',
  purpleBg: '#f5f3ff',
  pink: '#ec4899',
  pinkBg: '#fdf2f8',
  teal: '#14b8a6',
  tealBg: '#f0fdfa',
  overlay: 'rgba(10,14,39,0.5)',
  navy1: '#111b47',
  navy2: '#14205e',
  navy3: '#1a2f7a',
  navy4: '#111c4e',
};

function useReveal(threshold) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const obs = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          setVisible(true);
          obs.unobserve(node);
        }
      },
      { threshold: threshold ?? 0.15, rootMargin: '0px 0px -40px 0px' }
    );
    obs.observe(node);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, visible];
}

function useScrollSpy() {
  const [act, setAct] = useState('');
  useEffect(() => {
    const handle = () => {
      let cur = '';
      for (const s of SECCIONES) {
        const el = document.getElementById(s.id);
        if (el && el.getBoundingClientRect().top <= 120) cur = s.id;
      }
      setAct(cur);
    };
    window.addEventListener('scroll', handle, { passive: true });
    handle();
    return () => window.removeEventListener('scroll', handle);
  }, []);
  return act;
}

const SECCIONES = [
  { id: 'hero', label: 'Inicio' },
  { id: 'piramide', label: 'Estructura' },
  { id: 'problema', label: 'Problema' },
  { id: 'publica', label: 'Clientes' },
  { id: 'bot', label: 'IA' },
  { id: 'pedidos', label: 'Pedidos' },
  { id: 'pos', label: 'POS' },
  { id: 'restaurante', label: 'Restaurante' },
  { id: 'delivery', label: 'Delivery' },
  { id: 'inventario', label: 'Inventario' },
  { id: 'operaciones', label: 'Operaciones' },
  { id: 'vendedores', label: 'Vendedores' },
  { id: 'reportes', label: 'Reportes' },
  { id: 'permisos', label: 'Permisos' },
  { id: 'asistencia', label: 'Asistencia' },
  { id: 'costo-ia', label: 'Costos IA' },
  { id: 'planes', label: 'Planes' },
  { id: 'credito', label: 'Crédito' },
  { id: 'roadmap', label: 'Futuro' },
];

const NAV_GROUPS = [
  {
    label: 'Producto',
    items: [
      { id: 'piramide', label: 'Estructura' },
      { id: 'problema', label: 'Problema' },
      { id: 'publica', label: 'Clientes' },
      { id: 'bot', label: 'Bot IA' },
      { id: 'pedidos', label: 'Pedidos' },
      { id: 'pos', label: 'POS' },
    ],
  },
  {
    label: 'Operaciones',
    items: [
      { id: 'restaurante', label: 'Restaurante' },
      { id: 'delivery', label: 'Delivery' },
      { id: 'inventario', label: 'Inventario' },
      { id: 'operaciones', label: 'Back-office' },
      { id: 'vendedores', label: 'Vendedores' },
      { id: 'reportes', label: 'Reportes' },
    ],
  },
  {
    label: 'Equipo',
    items: [
      { id: 'permisos', label: 'Permisos y Roles' },
      { id: 'asistencia', label: 'Asistencia QR' },
    ],
  },
  {
    label: 'Planes',
    items: [
      { id: 'costo-ia', label: 'Costos IA' },
      { id: 'planes', label: 'Planes' },
      { id: 'credito', label: 'Crédito' },
      { id: 'roadmap', label: 'Futuro' },
    ],
  },
];

function stickyStyle(stuck, menuOpen) {
  return {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    zIndex: 1000,
    transition: 'all 0.3s',
    background: stuck || menuOpen ? 'rgba(10,14,39,0.95)' : 'transparent',
    backdropFilter: stuck || menuOpen ? 'blur(14px)' : 'none',
    borderBottom: stuck && !menuOpen ? `1px solid rgba(255,255,255,0.08)` : '1px solid transparent',
    padding: '0 20px',
  };
}

const M = { maxWidth: '1200px', margin: '0 auto', padding: '0 clamp(16px,4vw,24px)' };
const SCT = { padding: 'clamp(48px,8vw,96px) 0' };

const SLIDES = [
  {
    icon: <FiZap size={34} />,
    color: COL.acc,
    titulo: '¿Qué es Agorax?',
    texto:
      'Todo tu negocio en una sola plataforma con inteligencia artificial: ventas, pedidos, inventario, delivery y equipo.',
  },
  {
    icon: <FiMessageCircle size={34} />,
    color: COL.acc,
    titulo: 'Chat que vende 24/7',
    texto:
      'El bot con IA responde al instante, toma pedidos y cierra ventas solo. Tus clientes nunca esperan.',
  },
  {
    icon: <FiGrid size={34} />,
    color: COL.green,
    titulo: 'Catálogo y pedidos',
    texto:
      'Tus clientes compran por chat o por catálogo visual. El pedido se registra y se sigue de principio a fin.',
  },
  {
    icon: <FiCreditCard size={34} />,
    color: COL.green,
    titulo: 'Cobros y pagos',
    texto:
      '9 métodos de pago nativos en dólares, bolívares y pesos. Comprobantes verificados por tu equipo.',
  },
  {
    icon: <FiShoppingCart size={34} />,
    color: COL.teal,
    titulo: 'Punto de venta (POS)',
    texto:
      'Vende en mostrador con cierres de caja, multi-moneda y control de stock en tiempo real.',
  },
  {
    icon: <FiTruck size={34} />,
    color: COL.teal,
    titulo: 'Delivery',
    texto:
      'Repartidores con rutas optimizadas. El cliente ve el estado de su pedido hasta la entrega.',
  },
  {
    icon: <FiPackage size={34} />,
    color: COL.purple,
    titulo: 'Inventario',
    texto:
      'Stock en tiempo real, alertas de agotados y sincronización con tu sistema actual (SQL, Excel, ERP).',
  },
  {
    icon: <FiUsers size={34} />,
    color: COL.purple,
    titulo: 'Tu equipo',
    texto:
      'Roles y permisos por persona, asistencia por QR y paneles según cada función. Cada quien ve lo suyo.',
  },
  {
    icon: <FiBarChart2 size={34} />,
    color: COL.amber,
    titulo: 'Reportes',
    texto:
      '8 reportes listos: ventas, inventario, cierres, clientes y más. Decide con datos, no con intuición.',
  },
  {
    icon: <FiDollarSign size={34} />,
    color: COL.amber,
    titulo: 'Crédito y comisiones',
    texto:
      'Cuentas por cobrar con cuotas y comisiones de vendedores automáticas. Control total de tu dinero.',
  },
  {
    icon: <FiClipboard size={34} />,
    color: COL.green,
    titulo: 'Precios claros',
    texto:
      '14 días de prueba gratuita. El costo por configuración se cobra al finalizar la prueba, junto con la primera mensualidad.',
  },
  {
    tipoSelector: true,
    color: COL.acc,
    titulo: '¿Qué tipo de empresa tienes?',
    texto:
      'Agorax es totalmente configurable y se adapta a cualquier tipo de negocio. Elige una opción y descubre los beneficios que más te ayudan.',
  },
];

const TIPOS_EMPRESA = [
  {
    key: 'restaurante',
    label: 'Restaurante',
    icon: <FiCoffee size={22} />,
    color: COL.teal,
    beneficios: [
      {
        t: 'Manejo de mesas',
        d: 'Mapa de mesas en tiempo real: libre, ocupada o reservada. Cada comanda ligada a su mesa.',
      },
      {
        t: 'Comandas a cocina',
        d: 'El mesero envía la orden directo a la cocina desde la tablet. Cero papeles.',
      },
      {
        t: 'Producción y control de costos',
        d: 'Registra recetas e insumos y conoce el costo real de cada plato.',
      },
      {
        t: 'Diferentes roles',
        d: 'Mesero, cocinero, cajero y admin. Cada quien con su panel y permisos.',
      },
      {
        t: 'Sistema en tablets',
        d: 'Funciona en tablets en mostrador, barra y cocina. Sin instalaciones complicadas.',
      },
    ],
  },
  {
    key: 'mayorista',
    label: 'Mayorista / Proveedor',
    icon: <FiBriefcase size={22} />,
    color: COL.acc,
    beneficios: [
      {
        t: 'Pedidos fáciles',
        d: 'Tus clientes (empresas) hacen su pedido solos por chat o catálogo, 24/7.',
      },
      {
        t: 'Registro único por cliente',
        d: 'Cada cliente empresa tiene su usuario y clave. Pedidos siempre identificados.',
      },
      {
        t: 'Cuentas por cobrar',
        d: 'Control total de lo que te deben: saldos, cuotas y vencimientos.',
      },
      {
        t: 'Crédito completo',
        d: 'Límites, plazos y abonos por cliente. El sistema valida antes de aprobar.',
      },
      {
        t: 'Vendedores y comisiones',
        d: 'Asigna vendedores a tus clientes y calcula comisiones automáticas.',
      },
    ],
  },
  {
    key: 'panaderia',
    label: 'Panadería',
    icon: <FiLayers size={22} />,
    color: COL.amber,
    beneficios: [
      {
        t: 'Producción diaria',
        d: 'Órdenes de producción por horneada: pan, dulces y tortas, con costo por unidad.',
      },
      {
        t: 'Control de costos e insumos',
        d: 'Sabe cuánto te cuesta cada pan: harina, levadura y mano de obra.',
      },
      {
        t: 'Mermas',
        d: 'Registra lo que no se vendió y controla las pérdidas de cada día.',
      },
      {
        t: 'Venta al detal y al mayor',
        d: 'POS en mostrador + catálogo para pedidos anticipados de tus clientes.',
      },
      {
        t: 'Combos y categorías',
        d: 'Combina productos (docenas, cajas) y organiza tu catálogo por categorías.',
      },
    ],
  },
  {
    key: 'productor',
    label: 'Productor',
    icon: <FiPackage size={22} />,
    color: COL.green,
    beneficios: [
      {
        t: 'Costo de producción',
        d: 'Monitorea cuánto te cuesta producir cada producto: insumos, proceso y tiempo.',
      },
      {
        t: 'Administrar producción',
        d: 'Órdenes de producción, costos y margen por lote.',
      },
      {
        t: 'Varios depósitos',
        d: 'Vende desde diferentes almacenes con stock independiente.',
      },
      {
        t: 'Venta multicanal',
        d: 'POS, catálogo y pedidos. Produce, almacena y vende en un solo sistema.',
      },
      {
        t: 'Reportes',
        d: 'Reportes de producción, ventas e inventario para decidir con datos.',
      },
    ],
  },
  {
    key: 'otro',
    label: 'Otro negocio',
    icon: <FiGrid size={22} />,
    color: COL.pink,
    beneficios: [
      {
        t: '100% configurable',
        d: 'Agorax se adapta a tu operación, sin importar el rubro. Tú decides qué módulos usar.',
      },
      {
        t: 'Catálogo a tu medida',
        d: 'Categorías, pesables, combos y etiquetas como tu negocio los necesite.',
      },
      {
        t: 'Roles y permisos propios',
        d: 'Define quién ve y hace qué, con control granular por área y por persona.',
      },
      {
        t: 'Métodos de pago personalizados',
        d: 'Crea tus propios métodos de pago, monedas y tasas de cambio.',
      },
      {
        t: 'Activa solo lo que usas',
        d: 'Chat IA, POS, delivery, inventario, reportes y más. Enciende lo que necesites.',
      },
    ],
  },
];

/* ───── PRESENTACION RAPIDA (slideshow) ───── */
const PR_KEYFRAMES = `
  @keyframes prFadeIn { from { opacity: 0; } to { opacity: 1; } }
  @keyframes prPopIn { from { opacity: 0; transform: translateY(28px) scale(0.96); } to { opacity: 1; transform: translateY(0) scale(1); } }
  @keyframes prSlideUp { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
  @keyframes prFloat { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-7px); } }
`;

function TipoSelector({ onSelect }) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
        gap: '14px',
        width: '100%',
        maxWidth: '640px',
        margin: '0 auto',
      }}
    >
      {TIPOS_EMPRESA.map((t, i) => (
        <button
          key={t.key}
          onClick={() => onSelect(t.key)}
          style={{
            background: COL.white,
            border: `1.5px solid ${COL.border}`,
            borderRadius: '18px',
            padding: '24px 16px',
            cursor: 'pointer',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '12px',
            fontFamily: 'inherit',
            boxShadow: '0 2px 10px rgba(11,26,74,0.05)',
            transition: 'all 0.25s cubic-bezier(0.4,0,0.2,1)',
            animation: `prSlideUp 0.4s ease ${i * 0.06}s both`,
          }}
          onMouseOver={(e) => {
            e.currentTarget.style.borderColor = t.color;
            e.currentTarget.style.transform = 'translateY(-5px)';
            e.currentTarget.style.boxShadow = `0 16px 32px ${t.color}30`;
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.borderColor = COL.border;
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 2px 10px rgba(11,26,74,0.05)';
          }}
        >
          <span
            style={{
              width: '54px',
              height: '54px',
              borderRadius: '16px',
              background: `linear-gradient(135deg, ${t.color}, ${t.color}bb)`,
              color: COL.white,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: `0 8px 20px ${t.color}45`,
            }}
          >
            {t.icon}
          </span>
          <span style={{ fontSize: '15px', fontWeight: 700, color: COL.pri, lineHeight: 1.25 }}>
            {t.label}
          </span>
        </button>
      ))}
    </div>
  );
}

function BeneficiosVista({ tipo, onVolver, onCta }) {
  return (
    <>
      <div
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          padding: '8px 24px 8px',
          overflowY: 'auto',
        }}
      >
        <div style={{ textAlign: 'center', marginBottom: '8px', animation: 'prSlideUp 0.35s ease' }}>
          <span
            style={{
              width: '60px',
              height: '60px',
              borderRadius: '18px',
              background: `linear-gradient(135deg, ${tipo.color}, ${tipo.color}cc)`,
              color: COL.white,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: `0 10px 24px ${tipo.color}45`,
              marginBottom: '14px',
            }}
          >
            {tipo.icon}
          </span>
          <h3
            style={{
              fontSize: 'clamp(22px,3vw,28px)',
              fontWeight: 900,
              color: COL.pri,
              margin: '0 0 6px',
              letterSpacing: '-0.5px',
            }}
          >
            Beneficios para {tipo.label}
          </h3>
          <p style={{ fontSize: '15px', color: COL.muted, margin: '0 0 20px' }}>
            Esto es lo que Agorax resalta para tu tipo de negocio.
          </p>
        </div>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))',
            gap: '14px',
            width: '100%',
            maxWidth: '820px',
            paddingBottom: '16px',
          }}
        >
          {tipo.beneficios.map((b, i) => (
            <div
              key={i}
              onMouseOver={(e) => {
                e.currentTarget.style.transform = 'translateY(-4px)';
                e.currentTarget.style.boxShadow = `0 14px 30px ${tipo.color}28`;
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 2px 10px rgba(11,26,74,0.05)';
              }}
              style={{
                background: COL.white,
                borderRadius: '16px',
                padding: '20px',
                border: `1px solid ${COL.border}`,
                boxShadow: '0 2px 10px rgba(11,26,74,0.05)',
                position: 'relative',
                overflow: 'hidden',
                transition: 'all 0.25s',
                animation: `prSlideUp 0.4s ease ${i * 0.06}s both`,
              }}
            >
              <div
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: '3px',
                  background: `linear-gradient(90deg, ${tipo.color}, ${tipo.color}00)`,
                }}
              />
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                <span
                  style={{
                    width: '30px',
                    height: '30px',
                    borderRadius: '10px',
                    background: `${tipo.color}18`,
                    color: tipo.color,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '13px',
                    fontWeight: 800,
                    flexShrink: 0,
                  }}
                >
                  {i + 1}
                </span>
                <div style={{ fontSize: '15px', fontWeight: 700, color: COL.pri }}>{b.t}</div>
              </div>
              <div style={{ fontSize: '13px', color: COL.muted, lineHeight: 1.55 }}>{b.d}</div>
            </div>
          ))}
        </div>

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            background: `linear-gradient(135deg, ${COL.pri}, ${COL.navy3})`,
            borderRadius: '16px',
            padding: '16px 20px',
            maxWidth: '820px',
            width: '100%',
            boxShadow: '0 12px 28px rgba(10,14,39,0.25)',
            animation: 'prSlideUp 0.4s ease 0.4s both',
          }}
        >
          <span
            style={{
              width: '40px',
              height: '40px',
              borderRadius: '12px',
              background: 'rgba(255,255,255,0.12)',
              color: COL.acc,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <FiGrid size={20} />
          </span>
          <p style={{ fontSize: '14px', color: 'rgba(255,255,255,0.85)', margin: 0, lineHeight: 1.5 }}>
            <strong style={{ color: COL.white }}>Totalmente configurable.</strong> Agorax se adapta a
            cualquier tipo de empresa: activa solo los módulos que necesitas, personaliza roles,
            métodos de pago y catálogo a tu medida.
          </p>
        </div>
      </div>
      <div
        style={{
          padding: '14px 24px 24px',
          display: 'flex',
          gap: '12px',
          justifyContent: 'center',
          flexWrap: 'wrap',
          borderTop: `1px solid ${COL.border}`,
        }}
      >
        <button
          onClick={onVolver}
          style={{
            background: COL.light,
            color: COL.pri,
            border: 'none',
            borderRadius: '12px',
            padding: '13px 22px',
            fontWeight: 600,
            fontSize: '14px',
            cursor: 'pointer',
            transition: 'all 0.2s',
          }}
          onMouseOver={(e) => (e.currentTarget.style.background = COL.border)}
          onMouseOut={(e) => (e.currentTarget.style.background = COL.light)}
        >
          Elegir otro tipo
        </button>
        <button
          onClick={onCta}
          style={{
            background: `linear-gradient(135deg, ${COL.green}, ${COL.greenDark})`,
            color: COL.white,
            border: 'none',
            borderRadius: '12px',
            padding: '13px 30px',
            fontWeight: 700,
            fontSize: '15px',
            cursor: 'pointer',
            boxShadow: `0 6px 20px ${COL.green}45`,
            transition: 'all 0.2s',
            display: 'inline-flex',
            alignItems: 'center',
          }}
          onMouseOver={(e) => (e.currentTarget.style.transform = 'translateY(-2px)')}
          onMouseOut={(e) => (e.currentTarget.style.transform = 'translateY(0)')}
        >
          Registrate Gratis <FiArrowRight size={16} style={{ marginLeft: '6px' }} />
        </button>
      </div>
    </>
  );
}

function PresentacionRapida({ open, onClose, onCta }) {
  const [idx, setIdx] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [tipoSel, setTipoSel] = useState(null);
  const bodyRef = useRef(null);

  useEffect(() => {
    if (!open) {
      setIdx(0);
      setPlaying(false);
      setTipoSel(null);
      document.body.style.overflow = '';
      return;
    }
    document.body.style.overflow = 'hidden';
    if (bodyRef.current) bodyRef.current.focus();
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowRight') {
        setPlaying(false);
        setIdx((p) => Math.min(p + 1, SLIDES.length - 1));
      } else if (e.key === 'ArrowLeft') {
        setPlaying(false);
        setIdx((p) => Math.max(p - 1, 0));
      }
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [open, onClose]);

  useEffect(() => {
    if (!open || !playing) return;
    const t = setInterval(() => {
      setIdx((p) => {
        if (p >= SLIDES.length - 1) {
          setPlaying(false);
          return p;
        }
        return p + 1;
      });
    }, 5000);
    return () => clearInterval(t);
  }, [open, playing]);

  if (!open) return null;

  const slide = SLIDES[idx];
  const tipo = tipoSel ? TIPOS_EMPRESA.find((t) => t.key === tipoSel) : null;
  const esSelector = !tipo && slide.tipoSelector;
  const progress = ((idx + 1) / SLIDES.length) * 100;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Resumen de Agorax en diapositivas"
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 5000,
        background: 'radial-gradient(circle at 50% 30%, rgba(99,130,235,0.18), transparent 60%), rgba(10,14,39,0.9)',
        backdropFilter: 'blur(10px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
        animation: 'prFadeIn 0.25s ease',
      }}
    >
      <style>{PR_KEYFRAMES}</style>
      <div
        onClick={(e) => e.stopPropagation()}
        ref={bodyRef}
        tabIndex={-1}
        style={{
          background: COL.white,
          borderRadius: '24px',
          width: '100%',
          maxWidth: tipo ? '900px' : '680px',
          minHeight: '440px',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          outline: 'none',
          position: 'relative',
          boxShadow: '0 32px 80px rgba(0,0,0,0.45)',
          animation: 'prPopIn 0.35s cubic-bezier(0.4,0,0.2,1)',
        }}
      >
        {!tipo && (
          <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '4px', background: COL.light, zIndex: 2 }}>
            <div
              style={{
                height: '100%',
                width: `${progress}%`,
                background: `linear-gradient(90deg, ${slide.color}, ${COL.acc})`,
                borderRadius: '0 4px 4px 0',
                transition: 'width 0.4s ease',
              }}
            />
          </div>
        )}

        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '18px 20px 0',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span
              style={{
                display: 'inline-flex',
                padding: '4px 12px',
                borderRadius: '20px',
                background: tipo ? `${tipo.color}18` : `${slide.color}18`,
                color: tipo ? tipo.color : slide.color,
                fontSize: '12px',
                fontWeight: 700,
              }}
            >
              {tipo ? `Beneficios — ${tipo.label}` : `Paso ${idx + 1} de ${SLIDES.length}`}
            </span>
          </div>
          <button
            onClick={onClose}
            aria-label="Cerrar presentación"
            style={{
              background: COL.light,
              border: 'none',
              borderRadius: '50%',
              width: '38px',
              height: '38px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              color: COL.pri,
              transition: 'all 0.2s',
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.background = COL.red;
              e.currentTarget.style.color = COL.white;
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.background = COL.light;
              e.currentTarget.style.color = COL.pri;
            }}
          >
            <FiX size={18} />
          </button>
        </div>

        {tipo ? (
          <BeneficiosVista tipo={tipo} onVolver={() => setTipoSel(null)} onCta={onCta} />
        ) : esSelector ? (
          <>
            <div
              style={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
                padding: '24px 32px 16px',
              }}
            >
              <div
                key={idx}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  animation: 'prSlideUp 0.35s ease',
                }}
              >
                <div
                  style={{
                    width: '88px',
                    height: '88px',
                    borderRadius: '50%',
                    background: `linear-gradient(135deg, ${slide.color}, ${slide.color}bb)`,
                    color: COL.white,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: `0 16px 36px ${slide.color}50`,
                    marginBottom: '24px',
                    animation: 'prFloat 4s ease-in-out infinite',
                  }}
                >
                  <FiBriefcase size={38} />
                </div>
                <h3 style={{ fontSize: 'clamp(24px,3.2vw,30px)', fontWeight: 900, color: COL.pri, margin: '0 0 12px', letterSpacing: '-0.5px' }}>
                  {slide.titulo}
                </h3>
                <p style={{ fontSize: 'clamp(15px,2vw,17px)', color: COL.muted, lineHeight: 1.6, maxWidth: '520px', margin: '0 0 26px' }}>
                  {slide.texto}
                </p>
              </div>
              <TipoSelector onSelect={(key) => setTipoSel(key)} />
            </div>
            <div
              style={{
                padding: '8px 20px 22px',
                display: 'flex',
                flexDirection: 'column',
                gap: '16px',
                alignItems: 'center',
              }}
            >
              <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', justifyContent: 'center' }}>
                {SLIDES.map((s, i) => (
                  <button
                    key={i}
                    onClick={() => {
                      setPlaying(false);
                      setIdx(i);
                    }}
                    aria-label={`Ir a la diapositiva ${i + 1}: ${s.titulo}`}
                    style={{
                      width: i === idx ? '24px' : '8px',
                      height: '8px',
                      borderRadius: '4px',
                      border: 'none',
                      background: i === idx ? slide.color : COL.border,
                      cursor: 'pointer',
                      transition: 'all 0.25s',
                    }}
                  />
                ))}
              </div>
              <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                <button
                  onClick={() => {
                    setPlaying(false);
                    setIdx((p) => Math.max(p - 1, 0));
                  }}
                  aria-label="Diapositiva anterior"
                  style={navBtnStyle(false)}
                >
                  <FiChevronLeft size={20} />
                </button>
              </div>
            </div>
          </>
        ) : (
          <>
            <div
              key={idx}
              style={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
                padding: '24px 32px 16px',
                animation: 'prSlideUp 0.35s ease',
              }}
            >
              <div
                style={{
                  width: '88px',
                  height: '88px',
                  borderRadius: '50%',
                  background: `linear-gradient(135deg, ${slide.color}, ${slide.color}bb)`,
                  color: COL.white,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: `0 16px 36px ${slide.color}50`,
                  marginBottom: '24px',
                  animation: 'prFloat 4s ease-in-out infinite',
                }}
              >
                {slide.icon}
              </div>
              <h3 style={{ fontSize: 'clamp(24px,3.2vw,30px)', fontWeight: 900, color: COL.pri, margin: '0 0 14px', letterSpacing: '-0.5px' }}>
                {slide.titulo}
              </h3>
              <p style={{ fontSize: 'clamp(15px,2vw,17px)', color: COL.muted, lineHeight: 1.6, maxWidth: '520px', margin: 0 }}>
                {slide.texto}
              </p>
            </div>

            <div
              style={{
                padding: '8px 20px 22px',
                display: 'flex',
                flexDirection: 'column',
                gap: '16px',
                alignItems: 'center',
              }}
            >
              <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', justifyContent: 'center' }}>
                {SLIDES.map((s, i) => (
                  <button
                    key={i}
                    onClick={() => {
                      setPlaying(false);
                      setIdx(i);
                    }}
                    aria-label={`Ir a la diapositiva ${i + 1}: ${s.titulo}`}
                    style={{
                      width: i === idx ? '24px' : '8px',
                      height: '8px',
                      borderRadius: '4px',
                      border: 'none',
                      background: i === idx ? slide.color : COL.border,
                      cursor: 'pointer',
                      transition: 'all 0.25s',
                    }}
                  />
                ))}
              </div>
              <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                <button
                  onClick={() => {
                    setPlaying(false);
                    setIdx((p) => Math.max(p - 1, 0));
                  }}
                  disabled={idx === 0}
                  aria-label="Diapositiva anterior"
                  style={navBtnStyle(idx === 0)}
                >
                  <FiChevronLeft size={20} />
                </button>
                <button
                  onClick={() => setPlaying((p) => !p)}
                  aria-label={playing ? 'Pausar' : 'Reproducir'}
                  style={{
                    background: `linear-gradient(135deg, ${slide.color}, ${slide.color}bb)`,
                    color: COL.white,
                    border: 'none',
                    borderRadius: '50%',
                    width: '52px',
                    height: '52px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    boxShadow: `0 8px 20px ${slide.color}55`,
                    transition: 'all 0.2s',
                  }}
                  onMouseOver={(e) => (e.currentTarget.style.transform = 'scale(1.06)')}
                  onMouseOut={(e) => (e.currentTarget.style.transform = 'scale(1)')}
                >
                  {playing ? <FiPause size={22} /> : <FiPlay size={22} />}
                </button>
                <button
                  onClick={() => {
                    setPlaying(false);
                    setIdx((p) => Math.min(p + 1, SLIDES.length - 1));
                  }}
                  disabled={idx === SLIDES.length - 1}
                  aria-label="Diapositiva siguiente"
                  style={navBtnStyle(idx === SLIDES.length - 1)}
                >
                  <FiChevronRight size={20} />
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function navBtnStyle(disabled) {
  return {
    background: COL.white,
    color: disabled ? COL.border : COL.pri,
    border: `1px solid ${COL.border}`,
    borderRadius: '50%',
    width: '46px',
    height: '46px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: disabled ? 'default' : 'pointer',
    opacity: disabled ? 0.45 : 1,
    boxShadow: disabled ? 'none' : '0 4px 14px rgba(11,26,74,0.12)',
    transition: 'all 0.2s',
  };
}

/* ───── HERO ───── */
function Hero({ onAbrirResumen }) {
  const [ref, v] = useReveal(0.1);
  const nav = useNavigate();
  return (
    <section
      id="hero"
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: `linear-gradient(160deg, ${COL.pri} 0%, ${COL.navy1} 30%, ${COL.navy2} 60%, ${COL.navy3} 100%)`,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background:
            'radial-gradient(ellipse 80% 60% at 50% 30%, rgba(99,130,235,0.12), transparent 70%)',
        }}
      />
      <div style={M}>
        <div
          ref={ref}
          style={{
            textAlign: 'center',
            position: 'relative',
            zIndex: 2,
            opacity: v ? 1 : 0,
            transform: v ? 'translateY(0)' : 'translateY(30px)',
            transition: 'all 0.8s ease',
          }}
        >
          <div
            style={{
              display: 'inline-block',
              background: 'rgba(99,130,235,0.15)',
              border: '1px solid rgba(99,130,235,0.25)',
              padding: '6px 16px',
              borderRadius: '20px',
              fontSize: 'clamp(12px,1.4vw,13px)',
              fontWeight: 600,
              color: COL.acc,
              letterSpacing: '0.5px',
              marginBottom: '28px',
            }}
          >
            Plataforma de Gestión Empresarial con IA
          </div>
          <h1
            style={{
              fontSize: 'clamp(32px,7vw,68px)',
              fontWeight: 900,
              color: COL.white,
              lineHeight: 1.08,
              margin: '0 0 20px',
              letterSpacing: '-1px',
            }}
          >
            AGORAX
          </h1>
          <p
            style={{
              fontSize: 'clamp(16px,2.2vw,20px)',
              color: 'rgba(255,255,255,0.60)',
              maxWidth: '700px',
              margin: '0 auto 36px',
              lineHeight: 1.6,
            }}
          >
            Automatiza la atención al cliente con IA, gestiona pedidos multi-sede, coordina
            deliverys, controla tu punto de venta, registra asistencia del personal y sincroniza tu
            inventario. Todo desde una sola plataforma.
          </p>
          <div style={{ display: 'flex', gap: '14px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <button
              onClick={() => nav('/registro')}
              style={{
                background: `linear-gradient(135deg,${COL.green},${COL.greenDark})`,
                color: COL.white,
                border: 'none',
                borderRadius: '12px',
                padding: '14px 32px',
                fontWeight: 700,
                fontSize: 'clamp(14px,1.6vw,15px)',
                cursor: 'pointer',
                boxShadow: `0 4px 20px ${COL.green}40`,
                transition: 'all 0.2s',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '10px',
                minHeight: '48px',
              }}
              onMouseOver={(e) => (e.currentTarget.style.transform = 'translateY(-2px)')}
              onMouseOut={(e) => (e.currentTarget.style.transform = 'translateY(0)')}
            >
              <FiArrowRight size={16} /> Registrate Gratis
            </button>
            <button
              onClick={() =>
                document.getElementById('planes')?.scrollIntoView({ behavior: 'smooth' })
              }
              style={{
                background: 'rgba(255,255,255,0.08)',
                color: COL.white,
                border: '1px solid rgba(255,255,255,0.15)',
                borderRadius: '12px',
                padding: '14px 32px',
                fontWeight: 600,
                fontSize: 'clamp(14px,1.6vw,15px)',
                cursor: 'pointer',
                transition: 'all 0.2s',
                minHeight: '48px',
              }}
            >
              Ver Planes
            </button>
            <button
              onClick={onAbrirResumen}
              style={{
                background: `linear-gradient(135deg,${COL.amber},#f59e0b)`,
                color: COL.pri,
                border: 'none',
                borderRadius: '12px',
                padding: '14px 32px',
                fontWeight: 700,
                fontSize: 'clamp(14px,1.6vw,15px)',
                cursor: 'pointer',
                boxShadow: `0 4px 20px ${COL.amber}50`,
                transition: 'all 0.2s',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '10px',
                minHeight: '48px',
              }}
              onMouseOver={(e) => (e.currentTarget.style.transform = 'translateY(-2px)')}
              onMouseOut={(e) => (e.currentTarget.style.transform = 'translateY(0)')}
            >
              <FiPlay size={16} /> Ver resumen en 1 minuto
            </button>
          </div>
          <div
            style={{
              marginTop: 'clamp(40px,8vw,72px)',
              display: 'flex',
              gap: 'clamp(24px,5vw,56px)',
              justifyContent: 'center',
              flexWrap: 'wrap',
            }}
          >
            {[
              { n: '24/7', l: 'Atención automática' },
              { n: '9', l: 'Roles operativos' },
              { n: '3', l: 'Monedas nativas' },
              { n: '9', l: 'Métodos de pago' },
              { n: '30+', l: 'Módulos integrados' },
              { n: '14d', l: 'Prueba gratuita' },
            ].map((s) => (
              <div key={s.n} style={{ textAlign: 'center' }}>
                <div
                  style={{ fontSize: 'clamp(24px,4vw,32px)', fontWeight: 800, color: COL.white }}
                >
                  {s.n}
                </div>
                <div
                  style={{
                    fontSize: 'clamp(11px,1.2vw,12px)',
                    color: 'rgba(255,255,255,0.45)',
                    marginTop: '4px',
                  }}
                >
                  {s.l}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div
        style={{
          position: 'absolute',
          bottom: '28px',
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 2,
        }}
      >
        <div
          style={{
            animation: 'bounce 2s infinite',
            opacity: 0.5,
            color: COL.white,
            fontSize: '24px',
          }}
        >
          &#8595;
        </div>
      </div>
    </section>
  );
}

/* ───── PIRAMIDE ───── */
function Piramide() {
  const [ref, v] = useReveal();
  const [hover, setHover] = useState(null);
  const niveles = [
    { label: 'AGORAX', sub: 'Plataforma central', size: 20, color: COL.pri2, items: 1 },
    {
      label: 'Empresas',
      sub: 'Schemas aislados en PostgreSQL',
      size: 18,
      color: COL.acc,
      items: 3,
    },
    {
      label: 'Sedes',
      sub: 'Sucursales con inventario propio',
      size: 16,
      color: COL.acc2,
      items: 6,
    },
    { label: 'Admin', sub: 'Control total por empresa', size: 14, color: COL.green, items: 1 },
    { label: 'Supervisor', sub: 'Gerencia de sede', size: 14, color: COL.amber, items: 4 },
    {
      label: 'Asesor / Armador / Delivery',
      sub: 'Atencion, armado y entregas',
      size: 13,
      color: COL.purple,
      items: 8,
    },
  ];

  const dim = (i) => hover !== null && hover !== i;

  return (
    <section id="piramide" style={{ ...SCT, background: COL.white }}>
      <div style={M}>
        <div
          ref={ref}
          style={{
            opacity: v ? 1 : 0,
            transform: v ? 'translateY(0)' : 'translateY(30px)',
            transition: 'all 0.6s ease',
          }}
        >
          <h2
            style={{
              fontSize: 'clamp(24px,3.5vw,36px)',
              fontWeight: 800,
              color: COL.pri,
              textAlign: 'center',
              margin: '0 0 8px',
            }}
          >
            Estructura Jerárquica
          </h2>
          <p
            style={{
              fontSize: 'clamp(14px,1.8vw,16px)',
              color: COL.muted,
              textAlign: 'center',
              maxWidth: '560px',
              margin: '0 auto 48px',
              lineHeight: 1.6,
            }}
          >
            Cada empresa tiene sus propias sedes, usuarios y datos completamente aislados. La
            pirámide refleja la jerarquía operativa real.
          </p>
          <div
            style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px' }}
          >
            {niveles.map((n, i) => (
              <div
                key={i}
                style={{
                  width: `${Math.max(140, 320 - i * 22)}px`,
                  maxWidth: '90vw',
                  position: 'relative',
                }}
              >
                <div
                  style={{
                    background:
                      hover === i ? `linear-gradient(135deg,${COL.pri2},${COL.acc})` : COL.bg,
                    borderRadius: '10px',
                    padding: 'clamp(10px,1.5vw,16px) clamp(14px,2vw,24px)',
                    textAlign: 'center',
                    cursor: 'default',
                    border: hover === i ? `2px solid ${COL.acc}` : `1px solid ${COL.border}`,
                    boxShadow: hover === i ? `0 8px 30px ${COL.acc}20` : 'none',
                    transform: hover === i ? 'scale(1.03)' : 'scale(1)',
                    transition: 'all 0.3s ease',
                    opacity: dim(i) ? 0.5 : 1,
                  }}
                  onMouseEnter={() => setHover(i)}
                  onMouseLeave={() => setHover(null)}
                >
                  <div
                    style={{
                      fontSize: `clamp(${n.size - 4}px,${n.size / 10}vw,${n.size}px)`,
                      fontWeight: 700,
                      color: hover === i ? COL.white : COL.pri,
                    }}
                  >
                    {n.label}
                    {i === 0 && (
                      <span
                        style={{
                          fontSize: '11px',
                          fontWeight: 600,
                          color: hover === i ? 'rgba(255,255,255,0.7)' : COL.acc,
                          marginLeft: '8px',
                          verticalAlign: 'middle',
                        }}
                      >
                        Actual
                      </span>
                    )}
                    {i === 5 && (
                      <span
                        style={{
                          fontSize: '10px',
                          fontWeight: 600,
                          color: hover === i ? 'rgba(255,255,255,0.7)' : COL.green,
                          marginLeft: '6px',
                          verticalAlign: 'middle',
                        }}
                      >
                        Nuevo
                      </span>
                    )}
                  </div>
                  <div
                    style={{
                      fontSize: 'clamp(11px,1.2vw,12px)',
                      color: hover === i ? 'rgba(255,255,255,0.6)' : COL.muted,
                      marginTop: '2px',
                    }}
                  >
                    {n.sub}
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div
            style={{
              marginTop: '48px',
              padding: 'clamp(16px,2vw,24px)',
              background: `linear-gradient(135deg,${COL.purpleBg},${COL.purpleLight})`,
              borderRadius: '14px',
              border: `1px solid ${COL.purpleBorder}`,
              textAlign: 'center',
            }}
          >
            <div
              style={{
                fontWeight: 700,
                color: COL.purple,
                fontSize: 'clamp(14px,1.6vw,15px)',
                marginBottom: '4px',
              }}
            >
              Proximamente
            </div>
            <p
              style={{
                fontSize: 'clamp(13px,1.4vw,14px)',
                color: COL.purpleDeep,
                margin: 0,
                lineHeight: 1.5,
              }}
            >
              Nuevo rol <strong>Empresario</strong> por encima del Admin con panel multi-empresa y
              reportes consolidados de todos sus negocios, deliverys y métricas en un solo lugar.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ───── PROBLEMA + SOLUCION ───── */
function ProblemaSolucion() {
  const [ref, v] = useReveal();
  const [flipped, setFlipped] = useState(null);

  const problemas = [
    {
      icon: <FiMessageCircle size={22} />,
      t: 'Atención manual lenta',
      d: 'Clientes esperando. Mensajes sin responder. Personal saturado.',
      sd: 'El bot IA atiende 24/7 al instante. Responde preguntas, toma pedidos y cierra ventas automáticamente.',
    },
    {
      icon: <FiUsers size={22} />,
      t: 'Expansión difícil',
      d: 'Nuevas sedes requieren replicar procesos manualmente. Cada sede tiene su propia dinámica.',
      sd: 'Esquema automático: agregas sedes, usuarios y configuración. El sistema replica la estructura operativa.',
    },
    {
      icon: <FiBarChart2 size={22} />,
      t: 'Inventario desactualizado',
      d: 'No sabes que hay en stock. Productos agotados que el cliente pide.',
      sd: 'Sincronización en tiempo real con tu base de datos externa. Alertas de stock bajo y agotados.',
    },
    {
      icon: <FiDollarSign size={22} />,
      t: 'Pérdida de ventas',
      d: 'Clientes que abandonan el chat. Pedidos mal armados. Sin seguimiento.',
      sd: 'Funnel de ventas de 5 fases. El bot no abandona al cliente. Cada pedido queda registrado.',
    },
    {
      icon: <FiClock size={22} />,
      t: 'Control de personal',
      d: 'No sabes quien llego a tiempo. Asistencia en papel. Horarios descontrolados.',
      sd: 'Asistencia por QR desde cada sede. Reportes diarios y mensuales. Justificación de faltas.',
    },
    {
      icon: <FiCreditCard size={22} />,
      t: 'Cuentas por cobrar',
      d: 'Clientes que pagan a crédito sin control. Cuotas perdidas. Deudas sin seguimiento.',
      sd: 'Sistema de crédito con cuotas y plazos. Control total de cuentas por cobrar directamente en Agorax.',
    },
  ];

  return (
    <section id="problema" style={{ ...SCT, background: COL.bg }}>
      <div style={M}>
        <div
          ref={ref}
          style={{
            opacity: v ? 1 : 0,
            transform: v ? 'translateY(0)' : 'translateY(30px)',
            transition: 'all 0.6s ease',
          }}
        >
          <h2
            style={{
              fontSize: 'clamp(24px,3.5vw,36px)',
              fontWeight: 800,
              color: COL.pri,
              textAlign: 'center',
              margin: '0 0 8px',
            }}
          >
            Problemas que Resolvemos
          </h2>
          <p
            style={{
              fontSize: 'clamp(14px,1.8vw,16px)',
              color: COL.muted,
              textAlign: 'center',
              maxWidth: '560px',
              margin: '0 auto 40px',
              lineHeight: 1.6,
            }}
          >
            Haz clic en cada carta para ver como Agorax lo soluciona.
          </p>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: `repeat(auto-fit, minmax(min(100%,260px), 1fr))`,
              gap: 'clamp(14px,2vw,20px)',
            }}
          >
            {problemas.map((p, i) => (
              <div
                key={i}
                role="button"
                tabIndex={0}
                aria-pressed={flipped === i}
                aria-label={`${p.t}. ${flipped === i ? 'Mostrando solución' : 'Ver solución'}`}
                onClick={() => setFlipped(flipped === i ? null : i)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setFlipped(flipped === i ? null : i);
                  }
                }}
                className="flip-card"
                style={{ cursor: 'pointer', perspective: '1000px', minHeight: '340px' }}
              >
                <div
                  style={{
                    position: 'relative',
                    width: '100%',
                    height: '100%',
                    transformStyle: 'preserve-3d',
                    transition: 'transform 0.5s',
                    transform: flipped === i ? 'rotateY(180deg)' : 'rotateY(0)',
                    minHeight: '340px',
                  }}
                >
                  <div
                    style={{
                      position: 'absolute',
                      inset: 0,
                      backfaceVisibility: 'hidden',
                      background: COL.white,
                      borderRadius: '14px',
                      padding: 'clamp(20px,3vw,28px)',
                      border: `1px solid ${COL.border}`,
                      boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      textAlign: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <div
                      style={{
                        width: '48px',
                        height: '48px',
                        borderRadius: '12px',
                        background: COL.redBg,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: COL.red,
                        marginBottom: '16px',
                      }}
                    >
                      {p.icon}
                    </div>
                    <h3
                      style={{
                        fontSize: 'clamp(16px,1.8vw,18px)',
                        fontWeight: 700,
                        color: COL.pri,
                        margin: '0 0 8px',
                      }}
                    >
                      {p.t}
                    </h3>
                    <p
                      style={{
                        fontSize: 'clamp(13px,1.4vw,14px)',
                        color: COL.muted,
                        lineHeight: 1.6,
                      }}
                    >
                      {p.d}
                    </p>
                    <div
                      style={{
                        marginTop: '16px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        color: COL.acc,
                        fontSize: '13px',
                        fontWeight: 600,
                      }}
                    >
                      Ver solución <FiChevronRight size={14} />
                    </div>
                  </div>
                  <div
                    style={{
                      position: 'absolute',
                      inset: 0,
                      backfaceVisibility: 'hidden',
                      transform: 'rotateY(180deg)',
                      background: `linear-gradient(135deg,${COL.pri2},${COL.acc})`,
                      borderRadius: '14px',
                      padding: 'clamp(20px,3vw,28px)',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      textAlign: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <div
                      style={{
                        width: '48px',
                        height: '48px',
                        borderRadius: '12px',
                        background: 'rgba(255,255,255,0.15)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: COL.white,
                        marginBottom: '16px',
                      }}
                    >
                      <FiZap size={22} />
                    </div>
                    <h3
                      style={{
                        fontSize: 'clamp(16px,1.8vw,18px)',
                        fontWeight: 700,
                        color: COL.white,
                        margin: '0 0 8px',
                      }}
                    >
                      Solución
                    </h3>
                    <p
                      style={{
                        fontSize: 'clamp(13px,1.5vw,14px)',
                        color: 'rgba(255,255,255,0.85)',
                        lineHeight: 1.6,
                      }}
                    >
                      {p.sd}
                    </p>
                    <div
                      style={{
                        marginTop: '16px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        color: 'rgba(255,255,255,0.7)',
                        fontSize: '13px',
                        fontWeight: 600,
                      }}
                    >
                      Volver al problema
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ───── AREA PUBLICA ───── */
function AreaPublica() {
  const [ref, v] = useReveal();
  const [tab, setTab] = useState(0);
  const tabs = [
    {
      icon: <FiMessageCircle size={18} />,
      label: 'Modo Chat',
      color: COL.acc,
      bg: COL.indigoBg,
      sp1: 'El cliente chatea con el bot IA.',
      sp2: 'Registro automático con nombre, cédula, teléfono y ubicación.',
      sp3: 'Pedidos sin salir del chat. El bot sugiere, arma carrito y cierra ventas.',
      sp4: 'Sube comprobantes de pago y recibe confirmación del asesor.',
    },
    {
      icon: <FiGrid size={18} />,
      label: 'Modo Catálogo',
      color: COL.green,
      bg: COL.greenBg,
      sp1: 'Exploración visual de productos por departamento.',
      sp2: 'Agrega al carrito con un clic. Ajusta cantidades fácilmente.',
      sp3: 'Selecciona dirección de entrega, sede cercana y método de pago.',
      sp4: 'Seguimiento del pedido en tiempo real, desde el registro hasta la entrega.',
    },
  ];
  return (
    <section id="publica" style={{ ...SCT, background: COL.white }}>
      <div style={M}>
        <div
          ref={ref}
          style={{
            opacity: v ? 1 : 0,
            transform: v ? 'translateY(0)' : 'translateY(30px)',
            transition: 'all 0.6s ease',
          }}
        >
          <h2
            style={{
              fontSize: 'clamp(24px,3.5vw,36px)',
              fontWeight: 800,
              color: COL.pri,
              textAlign: 'center',
              margin: '0 0 8px',
            }}
          >
            Área Pública — Tus Clientes
          </h2>
          <p
            style={{
              fontSize: 'clamp(14px,1.8vw,16px)',
              color: COL.muted,
              textAlign: 'center',
              maxWidth: '560px',
              margin: '0 auto 40px',
              lineHeight: 1.6,
            }}
          >
            Dos formas de comprar. Una misma experiencia de venta automatizada.
          </p>

          <div
            style={{ display: 'flex', gap: '12px', justifyContent: 'center', marginBottom: '36px' }}
          >
            {tabs.map((t, i) => (
              <button
                key={i}
                onClick={() => setTab(i)}
                style={{
                  background: tab === i ? t.bg : 'transparent',
                  border: tab === i ? `2px solid ${t.color}` : `1px solid ${COL.border}`,
                  borderRadius: '12px',
                  padding: '14px 28px',
                  cursor: 'pointer',
                  fontWeight: 600,
                  fontSize: 'clamp(13px,1.5vw,14px)',
                  color: tab === i ? t.color : COL.muted,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  transition: 'all 0.2s',
                  minHeight: '48px',
                }}
              >
                {t.icon} {t.label}
              </button>
            ))}
          </div>

          <div
            style={{
              background: COL.bg,
              borderRadius: '16px',
              border: `1px solid ${COL.border}`,
              padding: 'clamp(24px,4vw,40px)',
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                display: 'flex',
                gap: 'clamp(24px,4vw,40px)',
                alignItems: 'center',
                flexWrap: 'wrap',
              }}
            >
              <div style={{ flex: '1 1 300px' }}>
                <div
                  style={{
                    background: tabs[tab].bg,
                    borderRadius: '12px',
                    padding: '20px',
                    minHeight: '320px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '12px',
                    border: `1px solid ${tabs[tab].color}20`,
                  }}
                >
                  <div style={{ fontSize: '40px', color: tabs[tab].color }}>{tabs[tab].icon}</div>
                  <div
                    style={{ fontSize: 'clamp(15px,1.8vw,17px)', fontWeight: 700, color: COL.pri }}
                  >
                    {tabs[tab].label}
                  </div>
                  <div
                    style={{
                      width: '80%',
                      height: '4px',
                      background: COL.border,
                      borderRadius: '2px',
                    }}
                  >
                    <div
                      style={{
                        width: `${(tab + 1) * 15 + 25}%`,
                        height: '100%',
                        background: tabs[tab].color,
                        borderRadius: '2px',
                        transition: 'width 0.5s',
                      }}
                    />
                  </div>
                </div>
              </div>
              <div style={{ flex: '1 1 280px' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                  {[tabs[tab].sp1, tabs[tab].sp2, tabs[tab].sp3, tabs[tab].sp4].map((sp, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                      <div
                        style={{
                          width: '24px',
                          height: '24px',
                          borderRadius: '50%',
                          background: tabs[tab].bg,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                          color: tabs[tab].color,
                          fontSize: '12px',
                          fontWeight: 700,
                        }}
                      >
                        {i + 1}
                      </div>
                      <p
                        style={{
                          fontSize: 'clamp(13px,1.5vw,14px)',
                          color: COL.text,
                          margin: 0,
                          lineHeight: 1.5,
                        }}
                      >
                        {sp}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ───── BOT IA ───── */
function BotIA() {
  const [ref, v] = useReveal();
  const nav = useNavigate();
  const fases = [
    { n: '01', t: 'Bienvenida', d: 'Saludo cálido, construye confianza.' },
    { n: '02', t: 'Descubrimiento', d: 'Muestra categorías, explora necesidades.' },
    { n: '03', t: 'Negociación', d: 'Agrega al carrito, cross-selling, up-selling.' },
    { n: '04', t: 'Cierre', d: 'Resume pedido, totales, advierte no pagar antes de verificación.' },
    { n: '05', t: 'Post-Venta', d: 'Pedido creado. Agradece y ofrece re-orden.' },
  ];
  return (
    <section id="bot" style={{ ...SCT, background: COL.bg }}>
      <div style={M}>
        <div
          ref={ref}
          style={{
            opacity: v ? 1 : 0,
            transform: v ? 'translateY(0)' : 'translateY(30px)',
            transition: 'all 0.6s ease',
          }}
        >
          <h2
            style={{
              fontSize: 'clamp(24px,3.5vw,36px)',
              fontWeight: 800,
              color: COL.pri,
              textAlign: 'center',
              margin: '0 0 8px',
            }}
          >
            Bot Vendedor con IA
          </h2>
          <p
            style={{
              fontSize: 'clamp(14px,1.8vw,16px)',
              color: COL.muted,
              textAlign: 'center',
              maxWidth: '560px',
              margin: '0 auto 40px',
              lineHeight: 1.6,
            }}
          >
            DeepSeek AI potencia un bot que no solo responde.{' '}
            <strong style={{ color: COL.acc }}>Vende</strong>. Conoce tu inventario real y guia al
            cliente en un funnel de 5 fases.
          </p>

          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '0',
              position: 'relative',
              paddingLeft: 'clamp(20px,4vw,32px)',
            }}
          >
            <div
              style={{
                position: 'absolute',
                left: '15px',
                top: '28px',
                width: '2px',
                bottom: '28px',
                background: `linear-gradient(to bottom,${COL.acc},${COL.border})`,
              }}
            />
            {fases.map((f, i) => (
              <div
                key={i}
                style={{
                  display: 'flex',
                  gap: 'clamp(16px,2vw,24px)',
                  alignItems: 'flex-start',
                  paddingBottom: i < fases.length - 1 ? 'clamp(20px,3vw,32px)' : '0',
                  position: 'relative',
                }}
              >
                <div
                  style={{
                    width: 'clamp(28px,3vw,32px)',
                    height: 'clamp(28px,3vw,32px)',
                    borderRadius: '50%',
                    background: `linear-gradient(135deg,${COL.pri2},${COL.acc})`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: COL.white,
                    fontSize: 'clamp(11px,1.2vw,12px)',
                    fontWeight: 700,
                    flexShrink: 0,
                    zIndex: 1,
                    boxShadow: `0 4px 14px ${COL.acc}30`,
                  }}
                >
                  {f.n}
                </div>
                <div style={{ flex: 1 }}>
                  <h3
                    style={{
                      fontSize: 'clamp(15px,1.8vw,17px)',
                      fontWeight: 700,
                      color: COL.pri,
                      margin: '0 0 4px',
                    }}
                  >
                    {f.t}
                  </h3>
                  <p
                    style={{
                      fontSize: 'clamp(13px,1.4vw,14px)',
                      color: COL.muted,
                      margin: 0,
                      lineHeight: 1.5,
                    }}
                  >
                    {f.d}
                  </p>
                </div>
              </div>
            ))}
          </div>

          <div
            style={{
              marginTop: 'clamp(24px,4vw,36px)',
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%,240px), 1fr))',
              gap: '12px',
            }}
          >
            {[
              {
                t: 'Skills configurables',
                d: 'Temperatura, max tokens, nombre del bot, prompt personalizado. Sin tocar código.',
              },
              {
                t: 'Cache inteligente',
                d: 'Preguntas repetitivas se responden sin llamar a la IA. Ahorra hasta 40%.',
              },
              {
                t: 'Busqueda fuzzy',
                d: 'Tolera errores tipograficos. "arina pan" encuentra "harina pan".',
              },
              {
                t: 'Bot↔Humano',
                d: 'El bot transfiere al asesor cuando detecta atencion humana necesaria.',
              },
            ].map((x, i) => (
              <div
                key={i}
                style={{
                  background: `${COL.acc}08`,
                  borderRadius: '10px',
                  padding: '14px 16px',
                  border: `1px solid ${COL.acc}15`,
                }}
              >
                <div
                  style={{ fontSize: '14px', fontWeight: 700, color: COL.pri, marginBottom: '4px' }}
                >
                  {x.t}
                </div>
                <div style={{ fontSize: '13px', color: COL.muted, lineHeight: 1.5 }}>{x.d}</div>
              </div>
            ))}
          </div>

          <div style={{ marginTop: 'clamp(20px,3vw,28px)', textAlign: 'center' }}>
            <button
              onClick={() => nav('/detalle/bot-ia')}
              style={{
                background: COL.acc,
                color: COL.white,
                border: 'none',
                padding: '10px 24px',
                borderRadius: '10px',
                fontWeight: 600,
                fontSize: 'clamp(13px,1.4vw,14px)',
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                minHeight: '44px',
              }}
            >
              Ver detalle completo <FiArrowRight size={14} />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ───── PEDIDOS ───── */
function Pedidos() {
  const [ref, v] = useReveal();
  const nav = useNavigate();
  const scrollRef = useRef(null);
  const estados = [
    'Pendiente',
    'Revision',
    'Aprobado',
    'Esperando Pago',
    'Verificacion',
    'Verificado',
    'Armado',
    'Listo',
    'En Camino',
    'Llegada',
    'Entregado',
  ];

  const scroll = (dir) => {
    if (scrollRef.current) scrollRef.current.scrollBy({ left: dir * 240, behavior: 'smooth' });
  };

  return (
    <section id="pedidos" style={{ ...SCT, background: COL.white }}>
      <div style={M}>
        <div
          ref={ref}
          style={{
            opacity: v ? 1 : 0,
            transform: v ? 'translateY(0)' : 'translateY(30px)',
            transition: 'all 0.6s ease',
          }}
        >
          <h2
            style={{
              fontSize: 'clamp(24px,3.5vw,36px)',
              fontWeight: 800,
              color: COL.pri,
              textAlign: 'center',
              margin: '0 0 8px',
            }}
          >
            Pedidos y Métodos de Pago
          </h2>
          <p
            style={{
              fontSize: 'clamp(14px,1.8vw,16px)',
              color: COL.muted,
              textAlign: 'center',
              maxWidth: '560px',
              margin: '0 auto 40px',
              lineHeight: 1.6,
            }}
          >
            Trazabilidad de extremo a extremo, desde que el bot registra el pedido hasta la entrega.
            9 métodos de pago nativos en dólares, bolívares y pesos colombianos. Completamente
            configurables por sede.
          </p>

          <p
            style={{
              fontSize: 'clamp(12px,1.3vw,13px)',
              color: COL.muted,
              textAlign: 'center',
              marginBottom: '16px',
            }}
          >
            Desliza para ver el flujo completo del pedido &#8594;
          </p>
          <div style={{ position: 'relative' }}>
            <button
              onClick={() => scroll(-1)}
              aria-label="Desplazar pedidos a la izquierda"
              style={{
                position: 'absolute',
                left: '-8px',
                top: '50%',
                transform: 'translateY(-50%)',
                zIndex: 2,
                width: '32px',
                height: '32px',
                borderRadius: '50%',
                background: COL.white,
                border: `1px solid ${COL.border}`,
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: COL.pri,
              }}
            >
              &#8249;
            </button>
            <button
              onClick={() => scroll(1)}
              aria-label="Desplazar pedidos a la derecha"
              style={{
                position: 'absolute',
                right: '-8px',
                top: '50%',
                transform: 'translateY(-50%)',
                zIndex: 2,
                width: '32px',
                height: '32px',
                borderRadius: '50%',
                background: COL.white,
                border: `1px solid ${COL.border}`,
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: COL.pri,
              }}
            >
              &#8250;
            </button>
            <div
              ref={scrollRef}
              style={{
                display: 'flex',
                gap: '16px',
                overflowX: 'auto',
                padding: '16px 32px',
                scrollBehavior: 'smooth',
                scrollbarWidth: 'none',
                msOverflowStyle: 'none',
                WebkitOverflowScrolling: 'touch',
              }}
            >
              {estados.map((e, i) => (
                <div
                  key={i}
                  style={{
                    minWidth: '160px',
                    flexShrink: 0,
                    background: COL.bg,
                    borderRadius: '12px',
                    padding: '20px 16px',
                    border: `1px solid ${COL.border}`,
                    textAlign: 'center',
                  }}
                >
                  <div
                    style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '50%',
                      background: `linear-gradient(135deg,${COL.pri2},${COL.acc})`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: COL.white,
                      fontSize: '14px',
                      fontWeight: 700,
                      margin: '0 auto 10px',
                    }}
                  >
                    {i + 1}
                  </div>
                  <div style={{ fontSize: '14px', fontWeight: 700, color: COL.pri }}>{e}</div>
                </div>
              ))}
            </div>
          </div>

          <div
            style={{
              marginTop: 'clamp(32px,5vw,48px)',
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%,280px), 1fr))',
              gap: 'clamp(14px,2vw,20px)',
            }}
          >
            {[
              { t: 'Dolares', items: 'Efectivo USD, Binance, PayPal, Zelle', c: COL.acc },
              { t: 'Bolivares', items: 'Efectivo Bs, Pago Movil, Transferencia', c: COL.green },
              { t: 'Pesos Colombianos', items: 'Efectivo Pesos, Nequi', c: COL.amber },
            ].map((m, i) => (
              <div
                key={i}
                style={{
                  background: COL.bg,
                  borderRadius: '14px',
                  padding: 'clamp(16px,2vw,24px)',
                  border: `1px solid ${COL.border}`,
                  textAlign: 'center',
                }}
              >
                <div
                  style={{
                    fontWeight: 700,
                    color: m.c,
                    fontSize: 'clamp(15px,1.8vw,16px)',
                    marginBottom: '8px',
                  }}
                >
                  {m.t}
                </div>
                <div
                  style={{ fontSize: 'clamp(13px,1.4vw,14px)', color: COL.muted, lineHeight: 1.5 }}
                >
                  {m.items}
                </div>
              </div>
            ))}
          </div>

          <div
            style={{
              marginTop: '24px',
              padding: 'clamp(14px,2vw,18px)',
              background: COL.greenBg,
              borderRadius: '12px',
              border: `1px solid ${COL.greenBorder}`,
              textAlign: 'center',
            }}
          >
            <p
              style={{
                fontSize: 'clamp(13px,1.4vw,14px)',
                color: COL.green,
                fontWeight: 600,
                margin: 0,
              }}
            >
              Métodos de pago personalizados: cada empresa crea sus propios métodos con campos
              configurables por sede.
            </p>
          </div>

          <div style={{ textAlign: 'center', marginTop: '24px' }}>
            <button
              onClick={() => nav('/detalle/pedidos')}
              style={{
                background: 'transparent',
                color: COL.acc,
                border: `2px solid ${COL.acc}`,
                padding: '10px 24px',
                borderRadius: '10px',
                fontWeight: 600,
                fontSize: 'clamp(13px,1.4vw,14px)',
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                minHeight: '44px',
              }}
            >
              Ver detalle completo <FiArrowRight size={14} />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ───── POS ───── */
function PuntoDeVenta() {
  const [ref, v] = useReveal();
  return (
    <section id="pos" style={{ ...SCT, background: COL.bg }}>
      <div style={M}>
        <div
          ref={ref}
          style={{
            opacity: v ? 1 : 0,
            transform: v ? 'translateY(0)' : 'translateY(30px)',
            transition: 'all 0.6s ease',
          }}
        >
          <h2
            style={{
              fontSize: 'clamp(24px,3.5vw,36px)',
              fontWeight: 800,
              color: COL.pri,
              textAlign: 'center',
              margin: '0 0 8px',
            }}
          >
            Punto de Venta (POS)
          </h2>
          <p
            style={{
              fontSize: 'clamp(14px,1.8vw,16px)',
              color: COL.muted,
              textAlign: 'center',
              maxWidth: '560px',
              margin: '0 auto 40px',
              lineHeight: 1.6,
            }}
          >
            Sistema completo de caja registradora. Multi-moneda, cierres de caja, recibos y control
            de ventas en tiempo real.
          </p>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%,260px), 1fr))',
              gap: 'clamp(14px,2vw,20px)',
            }}
          >
            {[
              {
                icon: <FiShoppingCart size={22} />,
                t: 'Crear Venta',
                d: 'Registra ventas con multiples items, descuentos y métodos de pago simultáneos. Cálculo automático de cambio.',
              },
              {
                icon: <FiDollarSign size={22} />,
                t: 'Multi-Moneda',
                d: 'Vende en dólares, bolívares y pesos colombianos. Tasas de cambio configurables por empresa.',
              },
              {
                icon: <FiClock size={22} />,
                t: 'Cierres de Caja',
                d: 'Apertura y cierre de caja con arqueo. Control de efectivo, divisas y todos los métodos de pago del día.',
              },
              {
                icon: <FiDatabase size={22} />,
                t: 'Catalogo POS',
                d: 'Busqueda rapida de productos, favoritos, departamentos. Stock en tiempo real con cantidades comprometidas.',
              },
              {
                icon: <FiBarChart2 size={22} />,
                t: 'Historial de Ventas',
                d: 'Listado paginado de todas las ventas. Anulación con motivo. Exportación de reportes.',
              },
              {
                icon: <FiUsers size={22} />,
                t: 'Clientes en POS',
                d: 'Asigna ventas a clientes registrados. Busqueda por cédula, nombre o teléfono.',
              },
            ].map((x, i) => (
              <div
                key={i}
                style={{
                  background: COL.white,
                  borderRadius: '14px',
                  padding: 'clamp(16px,2vw,24px)',
                  border: `1px solid ${COL.border}`,
                  boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                }}
              >
                <div
                  style={{
                    width: '44px',
                    height: '44px',
                    borderRadius: '12px',
                    background: `${COL.green}15`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: COL.green,
                    marginBottom: '14px',
                  }}
                >
                  {x.icon}
                </div>
                <h3
                  style={{
                    fontSize: 'clamp(15px,1.7vw,16px)',
                    fontWeight: 700,
                    color: COL.pri,
                    margin: '0 0 6px',
                  }}
                >
                  {x.t}
                </h3>
                <p
                  style={{
                    fontSize: 'clamp(13px,1.4vw,14px)',
                    color: COL.muted,
                    lineHeight: 1.5,
                    margin: 0,
                  }}
                >
                  {x.d}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ───── DELIVERY ───── */
function Delivery() {
  const [ref, v] = useReveal();
  return (
    <section id="delivery" style={{ ...SCT, background: COL.white }}>
      <div style={M}>
        <div
          ref={ref}
          style={{
            opacity: v ? 1 : 0,
            transform: v ? 'translateY(0)' : 'translateY(30px)',
            transition: 'all 0.6s ease',
          }}
        >
          <h2
            style={{
              fontSize: 'clamp(24px,3.5vw,36px)',
              fontWeight: 800,
              color: COL.pri,
              textAlign: 'center',
              margin: '0 0 8px',
            }}
          >
            Delivery y Envio de Pedidos
          </h2>
          <p
            style={{
              fontSize: 'clamp(14px,1.8vw,16px)',
              color: COL.muted,
              textAlign: 'center',
              maxWidth: '560px',
              margin: '0 auto 40px',
              lineHeight: 1.6,
            }}
          >
            Coordinación de entregas con repartidores propios o de terceros. Panel Kanban y
            optimización de rutas.
          </p>

          <div
            style={{
              background: COL.bg,
              borderRadius: '16px',
              border: `1px solid ${COL.border}`,
              padding: 'clamp(20px,3vw,32px)',
              overflow: 'hidden',
            }}
          >
            <div style={{ display: 'flex', gap: 'clamp(20px,3vw,36px)', flexWrap: 'wrap' }}>
              <div style={{ flex: '1 1 280px' }}>
                <h3
                  style={{
                    fontSize: 'clamp(16px,1.8vw,18px)',
                    fontWeight: 700,
                    color: COL.pri,
                    margin: '0 0 16px',
                  }}
                >
                  Disponible Ahora
                </h3>
                {[
                  { t: 'Panel Kanban', d: '4 columnas: armado, listo, en camino, entregado hoy.' },
                  {
                    t: 'Asignacion de rutas',
                    d: 'Optimizacion de entregas con algoritmo Nearest Neighbor.',
                  },
                  {
                    t: 'Orden de entrega',
                    d: 'Secuencia optima de paradas calculada desde la sede.',
                  },
                  {
                    t: 'Delivery asignado',
                    d: 'Cada pedido se asigna a un repartidor específico.',
                  },
                  {
                    t: 'Rol Armador',
                    d: 'Panel dedicado para armar pedidos. Checklist persistente de items. Metricas.',
                  },
                ].map((x, i) => (
                  <div
                    key={i}
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '10px',
                      padding: '10px 0',
                      borderBottom: `1px solid ${COL.border}`,
                    }}
                  >
                    <FiTruck
                      size={16}
                      color={COL.green}
                      style={{ flexShrink: 0, marginTop: '2px' }}
                    />
                    <div>
                      <div style={{ fontSize: '14px', fontWeight: 600, color: COL.pri }}>{x.t}</div>
                      <div style={{ fontSize: '13px', color: COL.muted }}>{x.d}</div>
                    </div>
                  </div>
                ))}
              </div>
              <div
                style={{
                  flex: '1 1 240px',
                  background: 'rgba(245,158,11,0.04)',
                  borderRadius: '14px',
                  padding: 'clamp(16px,2vw,24px)',
                  border: `1px solid ${COL.amberBorder}`,
                }}
              >
                <div
                  style={{
                    display: 'inline-block',
                    padding: '3px 10px',
                    borderRadius: '6px',
                     background: COL.amberLight,
                    color: COL.amber,
                    fontSize: '11px',
                    fontWeight: 700,
                    marginBottom: '16px',
                  }}
                >
                  En Desarrollo
                </div>
                <h3
                  style={{
                    fontSize: 'clamp(16px,1.8vw,18px)',
                    fontWeight: 700,
                    color: COL.pri,
                    margin: '0 0 12px',
                  }}
                >
                  Proximamente
                </h3>
                <ul style={{ listStyle: 'none', padding: 0 }}>
                  {[
                    { t: 'GPS en Vivo', d: 'Ubicacion del repartidor en tiempo real en el mapa.' },
                    {
                      t: 'ETA Inteligente',
                      d: 'Tiempo estimado de entrega calculado dinámicamente.',
                    },
                  ].map((x, i) => (
                    <li
                      key={i}
                      style={{
                        padding: '8px 0',
                        fontSize: '13px',
                        color: COL.amberDeep,
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        lineHeight: 1.4,
                      }}
                    >
                      <FiStar size={12} color={COL.amber} style={{ flexShrink: 0 }} />{' '}
                      <span>
                        <strong>{x.t}:</strong> {x.d}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ───── INVENTARIO ───── */
function Inventario() {
  const [ref, v] = useReveal();
  const nav = useNavigate();
  const [counts, setCounts] = useState([0, 0, 0, 0, 0, 0]);
  useEffect(() => {
    const i = setInterval(() => {
      setCounts((c) =>
        c.map((x, j) =>
          Math.min(
            x + Math.floor(Math.random() * 40 + (j + 1) * 12),
            [2568, 48, 12, 135, 14, 782][j]
          )
        )
      );
    }, 2000);
    return () => clearInterval(i);
  }, []);

  return (
    <section id="inventario" style={{ ...SCT, background: COL.bg }}>
      <div style={M}>
        <div
          ref={ref}
          style={{
            opacity: v ? 1 : 0,
            transform: v ? 'translateY(0)' : 'translateY(30px)',
            transition: 'all 0.6s ease',
          }}
        >
          <h2
            style={{
              fontSize: 'clamp(24px,3.5vw,36px)',
              fontWeight: 800,
              color: COL.pri,
              textAlign: 'center',
              margin: '0 0 8px',
            }}
          >
            Inventario Inteligente
          </h2>
          <p
            style={{
              fontSize: 'clamp(14px,1.8vw,16px)',
              color: COL.muted,
              textAlign: 'center',
              maxWidth: '620px',
              margin: '0 auto 40px',
              lineHeight: 1.6,
            }}
          >
            Dashboard en tiempo real, alertas de stock, exportación a Excel, clasificación con IA y
            sincronización con tu base de datos externa (SQL Server, PostgreSQL, MySQL, Firebird).
          </p>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%,150px), 1fr))',
              gap: '12px',
              marginBottom: 'clamp(32px,4vw,48px)',
            }}
          >
            {[
              { l: 'Total Productos', v: counts[0], icon: FiPackage, c: COL.pri2 },
              { l: 'Agotados', v: counts[1], icon: FiX, c: COL.red },
              { l: 'Stock Negativo', v: counts[2], icon: FiX, c: COL.amber },
              { l: 'Pesables', v: counts[3], icon: FiGrid, c: COL.purple },
              { l: 'Deptos', v: counts[4], icon: FiBarChart2, c: COL.acc },
              { l: 'Clasificados IA', v: counts[5], icon: FiZap, c: COL.teal },
            ].map((m, i) => (
              <div
                key={i}
                style={{
                  background: COL.white,
                  borderRadius: '12px',
                  padding: 'clamp(12px,1.5vw,18px)',
                  border: `1px solid ${COL.border}`,
                  textAlign: 'center',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                }}
              >
                <div style={{ color: m.c, marginBottom: '6px' }}>
                  <m.icon size={20} />
                </div>
                <div style={{ fontSize: 'clamp(20px,3vw,28px)', fontWeight: 800, color: COL.pri }}>
                  {m.v.toLocaleString()}
                </div>
                <div style={{ fontSize: 'clamp(11px,1.2vw,12px)', color: COL.muted }}>{m.l}</div>
              </div>
            ))}
          </div>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%,280px), 1fr))',
              gap: '14px',
              marginBottom: '24px',
            }}
          >
            <div
              style={{
                background: `${COL.greenBg}`,
                borderRadius: '14px',
                padding: 'clamp(16px,2vw,24px)',
                border: `1px solid ${COL.greenBorder}`,
              }}
            >
              <p
                style={{
                  fontSize: 'clamp(14px,1.6vw,15px)',
                  color: COL.green,
                  fontWeight: 700,
                  margin: '0 0 4px',
                }}
              >
                Sincronizacion Externa
              </p>
              <p
                style={{
                  fontSize: 'clamp(13px,1.4vw,14px)',
                  color: COL.greenDeep,
                  margin: 0,
                  lineHeight: 1.5,
                }}
              >
                Conecta Agorax a tu base de datos (SQL Server, ERP). El inventario se actualiza
                automáticamente. Agente de escritorio con instalador y códigos de activación.
              </p>
            </div>
            <div
              style={{
                background: `${COL.purpleBg}`,
                borderRadius: '14px',
                padding: 'clamp(16px,2vw,24px)',
                border: `1px solid ${COL.purpleBorder}`,
              }}
            >
              <p
                style={{
                  fontSize: 'clamp(14px,1.6vw,15px)',
                  color: COL.purple,
                  fontWeight: 700,
                  margin: '0 0 4px',
                }}
              >
                Clasificacion con IA
              </p>
              <p
                style={{
                  fontSize: 'clamp(13px,1.4vw,14px)',
                  color: COL.purpleDeep,
                  margin: 0,
                  lineHeight: 1.5,
                }}
              >
                DeepSeek clasifica automáticamente tus productos por departamento. Clasificación
                individual o por lotes de hasta 500 productos.
              </p>
            </div>
          </div>

          <div style={{ textAlign: 'center' }}>
            <button
              onClick={() => nav('/detalle/inventario')}
              style={{
                background: 'transparent',
                color: COL.acc,
                border: `2px solid ${COL.acc}`,
                padding: '10px 24px',
                borderRadius: '10px',
                fontWeight: 600,
                fontSize: 'clamp(13px,1.4vw,14px)',
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                minHeight: '44px',
              }}
            >
              Ver detalle completo <FiArrowRight size={14} />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ───── ASISTENCIA ───── */
function Asistencia() {
  const [ref, v] = useReveal();
  return (
    <section id="asistencia" style={{ ...SCT, background: COL.white }}>
      <div style={M}>
        <div
          ref={ref}
          style={{
            opacity: v ? 1 : 0,
            transform: v ? 'translateY(0)' : 'translateY(30px)',
            transition: 'all 0.6s ease',
          }}
        >
          <h2
            style={{
              fontSize: 'clamp(24px,3.5vw,36px)',
              fontWeight: 800,
              color: COL.pri,
              textAlign: 'center',
              margin: '0 0 8px',
            }}
          >
            Asistencia por QR
          </h2>
          <p
            style={{
              fontSize: 'clamp(14px,1.8vw,16px)',
              color: COL.muted,
              textAlign: 'center',
              maxWidth: '560px',
              margin: '0 auto 40px',
              lineHeight: 1.6,
            }}
          >
            Control de asistencia del personal con códigos QR por sede. Registro, justificación de
            faltas, horarios y reportes exportables.
          </p>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%,260px), 1fr))',
              gap: 'clamp(14px,2vw,20px)',
            }}
          >
            {[
              {
                icon: <FiClock size={22} />,
                t: 'Registro QR',
                d: 'Cada sede tiene su propio código QR. El personal escanea para registrar entrada y salida. Rapido y seguro.',
              },
              {
                icon: <FiUsers size={22} />,
                t: 'Panel de Asistencia',
                d: 'Vista en tiempo real de quien esta presente, quien falto y quien llego tarde. Por sede y por empresa.',
              },
              {
                icon: <FiShield size={22} />,
                t: 'Justificación de Faltas',
                d: 'Registro manual de asistencias y justificación de ausencias por supervisor o admin.',
              },
              {
                icon: <FiBarChart2 size={22} />,
                t: 'Reportes',
                d: 'Reportes diarios, mensuales y por rango de fechas. Exportacion a PDF y CSV. Horas trabajadas por usuario.',
              },
              {
                icon: <FiDatabase size={22} />,
                t: 'Horarios',
                d: 'Gestión de horarios por sede. Configuración de horas de entrada, salida y tolerancia. Creación por lotes.',
              },
              {
                icon: <FiZap size={22} />,
                t: 'Gestión de QR',
                d: 'Creación, listado y revocación de códigos QR por sede. Seguridad ante pérdida o cambio de personal.',
              },
            ].map((x, i) => (
              <div
                key={i}
                style={{
                  background: COL.bg,
                  borderRadius: '14px',
                  padding: 'clamp(16px,2vw,24px)',
                  border: `1px solid ${COL.border}`,
                }}
              >
                <div
                  style={{
                    width: '44px',
                    height: '44px',
                    borderRadius: '12px',
                    background: `${COL.purple}15`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: COL.purple,
                    marginBottom: '14px',
                  }}
                >
                  {x.icon}
                </div>
                <h3
                  style={{
                    fontSize: 'clamp(15px,1.7vw,16px)',
                    fontWeight: 700,
                    color: COL.pri,
                    margin: '0 0 6px',
                  }}
                >
                  {x.t}
                </h3>
                <p
                  style={{
                    fontSize: 'clamp(13px,1.4vw,14px)',
                    color: COL.muted,
                    lineHeight: 1.5,
                    margin: 0,
                  }}
                >
                  {x.d}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ───── RESTAURANTE ───── */
function Restaurante() {
  const [ref, v] = useReveal();
  const items = [
    {
      icon: <FiGrid size={22} />,
      t: 'Mapa de Mesas',
      d: 'Mesas con estado en tiempo real: libre, ocupada o reservada. Comandas por mesa.',
    },
    {
      icon: <FiClipboard size={22} />,
      t: 'Comandas a Cocina',
      d: 'El mesero envía la orden directo a cocina con un clic. Sin papeles ni gritos.',
    },
    {
      icon: <FiZap size={22} />,
      t: 'KDS de Cocina',
      d: 'Pantalla de cocina en tiempo real. El cocinero marca cada plato como listo.',
    },
    {
      icon: <FiUsers size={22} />,
      t: 'Roles Mesero y Cocinero',
      d: 'Paneles dedicados por rol. El mesero atiende mesas, el cocinero gestiona preparación.',
    },
    {
      icon: <FiShoppingCart size={22} />,
      t: 'Pedidos en Sitio',
      d: 'Flujo de tienda completo: creado, comanda enviada, servido y cobrado.',
    },
    {
      icon: <FiCpu size={22} />,
      t: 'Bot Mesero Virtual',
      d: 'El bot IA atiende el chat con tu menú y toma pedidos de forma automática.',
    },
  ];
  return (
    <section id="restaurante" style={{ ...SCT, background: COL.bg }}>
      <div style={M}>
        <div
          ref={ref}
          style={{
            opacity: v ? 1 : 0,
            transform: v ? 'translateY(0)' : 'translateY(30px)',
            transition: 'all 0.6s ease',
          }}
        >
          <h2
            style={{
              fontSize: 'clamp(24px,3.5vw,36px)',
              fontWeight: 800,
              color: COL.pri,
              textAlign: 'center',
              margin: '0 0 8px',
            }}
          >
            Restaurante y Mesas
          </h2>
          <p
            style={{
              fontSize: 'clamp(14px,1.8vw,16px)',
              color: COL.muted,
              textAlign: 'center',
              maxWidth: '560px',
              margin: '0 auto 40px',
              lineHeight: 1.6,
            }}
          >
            El tipo de empresa Restaurante activa mesas, comandas, cocina y el bot Mesero Virtual.
            Todo el servicio en sitio, integrado.
          </p>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%,260px), 1fr))',
              gap: 'clamp(14px,2vw,20px)',
            }}
          >
            {items.map((x, i) => (
              <div
                key={i}
                style={{
                  background: COL.white,
                  borderRadius: '14px',
                  padding: 'clamp(16px,2vw,24px)',
                  border: `1px solid ${COL.border}`,
                  boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                }}
              >
                <div
                  style={{
                    width: '44px',
                    height: '44px',
                    borderRadius: '12px',
                    background: `${COL.teal}15`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: COL.teal,
                    marginBottom: '14px',
                  }}
                >
                  {x.icon}
                </div>
                <h3
                  style={{
                    fontSize: 'clamp(15px,1.7vw,16px)',
                    fontWeight: 700,
                    color: COL.pri,
                    margin: '0 0 6px',
                  }}
                >
                  {x.t}
                </h3>
                <p
                  style={{
                    fontSize: 'clamp(13px,1.4vw,14px)',
                    color: COL.muted,
                    lineHeight: 1.5,
                    margin: 0,
                  }}
                >
                  {x.d}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ───── OPERACIONES / BACK-OFFICE ───── */
function Operaciones() {
  const [ref, v] = useReveal();
  const items = [
    {
      icon: <FiLayers size={22} />,
      t: 'Producción',
      d: 'Órdenes de producción con costos, margen y control de mermas.',
    },
    {
      icon: <FiBriefcase size={22} />,
      t: 'Compras',
      d: 'Proveedores, órdenes de compra y recepción de mercancía.',
    },
    {
      icon: <FiDollarSign size={22} />,
      t: 'Tesorería',
      d: 'Control de caja, ingresos y egresos en un solo lugar.',
    },
    {
      icon: <FiPackage size={22} />,
      t: 'Depósitos',
      d: 'Múltiples almacenes con stock independiente y movimientos.',
    },
    {
      icon: <FiClipboard size={22} />,
      t: 'Órdenes de Compra',
      d: 'Seguimiento completo de compras a proveedores, de la solicitud a la recepción.',
    },
    {
      icon: <FiDatabase size={22} />,
      t: 'Inventario Integrado',
      d: 'El stock se actualiza automáticamente con cada compra, producción o venta.',
    },
  ];
  return (
    <section id="operaciones" style={{ ...SCT, background: COL.white }}>
      <div style={M}>
        <div
          ref={ref}
          style={{
            opacity: v ? 1 : 0,
            transform: v ? 'translateY(0)' : 'translateY(30px)',
            transition: 'all 0.6s ease',
          }}
        >
          <h2
            style={{
              fontSize: 'clamp(24px,3.5vw,36px)',
              fontWeight: 800,
              color: COL.pri,
              textAlign: 'center',
              margin: '0 0 8px',
            }}
          >
            Operaciones y Back-office
          </h2>
          <p
            style={{
              fontSize: 'clamp(14px,1.8vw,16px)',
              color: COL.muted,
              textAlign: 'center',
              maxWidth: '560px',
              margin: '0 auto 40px',
              lineHeight: 1.6,
            }}
          >
            Producción, compras, tesorería y depósitos. Todo el ciclo operativo de tu negocio bajo
            control.
          </p>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%,260px), 1fr))',
              gap: 'clamp(14px,2vw,20px)',
            }}
          >
            {items.map((x, i) => (
              <div
                key={i}
                style={{
                  background: COL.bg,
                  borderRadius: '14px',
                  padding: 'clamp(16px,2vw,24px)',
                  border: `1px solid ${COL.border}`,
                }}
              >
                <div
                  style={{
                    width: '44px',
                    height: '44px',
                    borderRadius: '12px',
                    background: `${COL.acc}15`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: COL.acc,
                    marginBottom: '14px',
                  }}
                >
                  {x.icon}
                </div>
                <h3
                  style={{
                    fontSize: 'clamp(15px,1.7vw,16px)',
                    fontWeight: 700,
                    color: COL.pri,
                    margin: '0 0 6px',
                  }}
                >
                  {x.t}
                </h3>
                <p
                  style={{
                    fontSize: 'clamp(13px,1.4vw,14px)',
                    color: COL.muted,
                    lineHeight: 1.5,
                    margin: 0,
                  }}
                >
                  {x.d}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ───── VENDEDORES Y COMISIONES ───── */
function Vendedores() {
  const [ref, v] = useReveal();
  const items = [
    {
      icon: <FiUserCheck size={22} />,
      t: 'Gestión de Vendedores',
      d: 'Crea vendedores, asígnales clientes y define sus porcentajes.',
    },
    {
      icon: <FiPercent size={22} />,
      t: 'Comisiones Automáticas',
      d: 'Cálculo de comisiones por porcentaje sobre cada venta.',
    },
    {
      icon: <FiClock size={22} />,
      t: 'Estados de Comisión',
      d: 'Devengada, liquidada, pagada o anulada. Trazabilidad total.',
    },
    {
      icon: <FiClipboard size={22} />,
      t: 'Liquidaciones',
      d: 'Liquidaciones en borrador, confirmadas o pagadas.',
    },
    {
      icon: <FiUsers size={22} />,
      t: 'Clientes Asignados',
      d: 'Cada vendedor ve su cartera de clientes y su historial.',
    },
    {
      icon: <FiShoppingCart size={22} />,
      t: 'Integración con Ventas',
      d: 'Las comisiones se calculan automáticamente sobre ventas y POS.',
    },
  ];
  return (
    <section id="vendedores" style={{ ...SCT, background: COL.bg }}>
      <div style={M}>
        <div
          ref={ref}
          style={{
            opacity: v ? 1 : 0,
            transform: v ? 'translateY(0)' : 'translateY(30px)',
            transition: 'all 0.6s ease',
          }}
        >
          <h2
            style={{
              fontSize: 'clamp(24px,3.5vw,36px)',
              fontWeight: 800,
              color: COL.pri,
              textAlign: 'center',
              margin: '0 0 8px',
            }}
          >
            Vendedores y Comisiones
          </h2>
          <p
            style={{
              fontSize: 'clamp(14px,1.8vw,16px)',
              color: COL.muted,
              textAlign: 'center',
              maxWidth: '560px',
              margin: '0 auto 40px',
              lineHeight: 1.6,
            }}
          >
            Motiva a tu equipo con comisiones claras y automáticas, ligadas a cada venta real.
          </p>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%,260px), 1fr))',
              gap: 'clamp(14px,2vw,20px)',
            }}
          >
            {items.map((x, i) => (
              <div
                key={i}
                style={{
                  background: COL.white,
                  borderRadius: '14px',
                  padding: 'clamp(16px,2vw,24px)',
                  border: `1px solid ${COL.border}`,
                  boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                }}
              >
                <div
                  style={{
                    width: '44px',
                    height: '44px',
                    borderRadius: '12px',
                    background: `${COL.green}15`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: COL.green,
                    marginBottom: '14px',
                  }}
                >
                  {x.icon}
                </div>
                <h3
                  style={{
                    fontSize: 'clamp(15px,1.7vw,16px)',
                    fontWeight: 700,
                    color: COL.pri,
                    margin: '0 0 6px',
                  }}
                >
                  {x.t}
                </h3>
                <p
                  style={{
                    fontSize: 'clamp(13px,1.4vw,14px)',
                    color: COL.muted,
                    lineHeight: 1.5,
                    margin: 0,
                  }}
                >
                  {x.d}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ───── REPORTES ───── */
function Reportes() {
  const [ref, v] = useReveal();
  const items = [
    { icon: <FiBarChart2 size={20} />, l: 'Ventas' },
    { icon: <FiPackage size={20} />, l: 'Inventario' },
    { icon: <FiLayers size={20} />, l: 'Producción' },
    { icon: <FiDollarSign size={20} />, l: 'Cierres de Caja' },
    { icon: <FiUsers size={20} />, l: 'Clientes' },
    { icon: <FiUserCheck size={20} />, l: 'Usuarios' },
    { icon: <FiCpu size={20} />, l: 'IA' },
    { icon: <FiBriefcase size={20} />, l: 'Compras' },
  ];
  return (
    <section id="reportes" style={{ ...SCT, background: COL.white }}>
      <div style={M}>
        <div
          ref={ref}
          style={{
            opacity: v ? 1 : 0,
            transform: v ? 'translateY(0)' : 'translateY(30px)',
            transition: 'all 0.6s ease',
          }}
        >
          <h2
            style={{
              fontSize: 'clamp(24px,3.5vw,36px)',
              fontWeight: 800,
              color: COL.pri,
              textAlign: 'center',
              margin: '0 0 8px',
            }}
          >
            Reportes e Inteligencia
          </h2>
          <p
            style={{
              fontSize: 'clamp(14px,1.8vw,16px)',
              color: COL.muted,
              textAlign: 'center',
              maxWidth: '560px',
              margin: '0 auto 40px',
              lineHeight: 1.6,
            }}
          >
            Ocho módulos de reportes para tomar decisiones con datos reales, no con intuición.
          </p>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%,140px), 1fr))',
              gap: '12px',
            }}
          >
            {items.map((x, i) => (
              <div
                key={i}
                style={{
                  background: COL.bg,
                  borderRadius: '12px',
                  padding: 'clamp(14px,2vw,20px)',
                  border: `1px solid ${COL.border}`,
                  textAlign: 'center',
                }}
              >
                <div style={{ color: COL.acc, marginBottom: '8px' }}>{x.icon}</div>
                <div style={{ fontSize: 'clamp(13px,1.4vw,14px)', fontWeight: 600, color: COL.pri }}>
                  {x.l}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ───── PERMISOS Y ROLES (RBAC) ───── */
function PermisosRoles() {
  const [ref, v] = useReveal();
  const items = [
    {
      icon: <FiUsers size={22} />,
      t: 'Roles Personalizados',
      d: 'Crea roles a medida y asigna permisos específicos por función.',
    },
    {
      icon: <FiLock size={22} />,
      t: 'Permisos Granulares',
      d: 'Control por página, sección y acción. Hasta el nivel de tarjeta de inventario.',
    },
    {
      icon: <FiShield size={22} />,
      t: 'Modo Solo-Lectura',
      d: 'Permite ver sin editar. Ideal para supervisores y reportes.',
    },
    {
      icon: <FiBell size={22} />,
      t: 'Notificaciones Push',
      d: 'Alertas al cliente y al equipo en cada cambio de estado del pedido.',
    },
    {
      icon: <FiGrid size={22} />,
      t: 'Permisos de Inventario',
      d: 'Secciones y tarjetas toggleables por rol. Cada quien ve lo que necesita.',
    },
    {
      icon: <FiDatabase size={22} />,
      t: 'Aislamiento por Empresa',
      d: 'Cada empresa en su propio schema PostgreSQL. Datos completamente aislados.',
    },
  ];
  return (
    <section id="permisos" style={{ ...SCT, background: COL.bg }}>
      <div style={M}>
        <div
          ref={ref}
          style={{
            opacity: v ? 1 : 0,
            transform: v ? 'translateY(0)' : 'translateY(30px)',
            transition: 'all 0.6s ease',
          }}
        >
          <h2
            style={{
              fontSize: 'clamp(24px,3.5vw,36px)',
              fontWeight: 800,
              color: COL.pri,
              textAlign: 'center',
              margin: '0 0 8px',
            }}
          >
            Permisos, Roles y Seguridad
          </h2>
          <p
            style={{
              fontSize: 'clamp(14px,1.8vw,16px)',
              color: COL.muted,
              textAlign: 'center',
              maxWidth: '560px',
              margin: '0 auto 40px',
              lineHeight: 1.6,
            }}
          >
            Control granular de accesos por rol y datos aislados por empresa. La seguridad que tu
            negocio necesita.
          </p>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%,260px), 1fr))',
              gap: 'clamp(14px,2vw,20px)',
            }}
          >
            {items.map((x, i) => (
              <div
                key={i}
                style={{
                  background: COL.white,
                  borderRadius: '14px',
                  padding: 'clamp(16px,2vw,24px)',
                  border: `1px solid ${COL.border}`,
                  boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                }}
              >
                <div
                  style={{
                    width: '44px',
                    height: '44px',
                    borderRadius: '12px',
                    background: `${COL.purple}15`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: COL.purple,
                    marginBottom: '14px',
                  }}
                >
                  {x.icon}
                </div>
                <h3
                  style={{
                    fontSize: 'clamp(15px,1.7vw,16px)',
                    fontWeight: 700,
                    color: COL.pri,
                    margin: '0 0 6px',
                  }}
                >
                  {x.t}
                </h3>
                <p
                  style={{
                    fontSize: 'clamp(13px,1.4vw,14px)',
                    color: COL.muted,
                    lineHeight: 1.5,
                    margin: 0,
                  }}
                >
                  {x.d}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ───── COSTO IA ───── */
function CostoIA() {
  const [ref, v] = useReveal();
  return (
    <section id="costo-ia" style={{ ...SCT, background: COL.bg }}>
      <div style={M}>
        <div
          ref={ref}
          style={{
            opacity: v ? 1 : 0,
            transform: v ? 'translateY(0)' : 'translateY(30px)',
            transition: 'all 0.6s ease',
          }}
        >
          <h2
            style={{
              fontSize: 'clamp(24px,3.5vw,36px)',
              fontWeight: 800,
              color: COL.pri,
              textAlign: 'center',
              margin: '0 0 8px',
            }}
          >
            Costo de la Inteligencia Artificial
          </h2>
          <p
            style={{
              fontSize: 'clamp(14px,1.8vw,16px)',
              color: COL.muted,
              textAlign: 'center',
              maxWidth: '600px',
              margin: '0 auto 40px',
              lineHeight: 1.6,
            }}
          >
            Cada empresa asigna su propia API key y es responsable de mantener su saldo. El cache
            inteligente reduce el consumo hasta un 40%.
          </p>

          <div
            style={{
              background: COL.white,
              borderRadius: '16px',
              border: `1px solid ${COL.border}`,
              padding: 'clamp(20px,3vw,32px)',
              overflow: 'hidden',
              boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
            }}
          >
            <div
              style={{
                display: 'flex',
                gap: 'clamp(20px,4vw,48px)',
                flexWrap: 'wrap',
                alignItems: 'center',
              }}
            >
              <div style={{ flex: '1 1 260px', textAlign: 'center' }}>
                <div style={{ fontSize: 'clamp(40px,6vw,56px)', fontWeight: 900, color: COL.pri2 }}>
                  ~$3{' '}
                  <span
                    style={{ fontSize: 'clamp(16px,2vw,20px)', fontWeight: 500, color: COL.muted }}
                  >
                    USD / mes
                  </span>
                </div>
                <p
                  style={{ fontSize: 'clamp(14px,1.6vw,15px)', color: COL.muted, lineHeight: 1.5 }}
                >
                  Costo operativo para atender 200-250 clientes activos con ~10,000 mensajes
                  procesados.
                </p>
                <div
                  style={{
                    marginTop: '20px',
                    padding: 'clamp(14px,2vw,20px)',
                    background: COL.greenBg,
                    borderRadius: '12px',
                    border: `1px solid ${COL.greenBorder}`,
                  }}
                >
                  <p
                    style={{
                      fontSize: 'clamp(13px,1.4vw,14px)',
                      color: COL.greenDeep,
                      fontWeight: 600,
                      margin: 0,
                    }}
                  >
                    En una conversacion tipica de 7 mensajes, 2-3 ya estan en cache = $0 de costo.
                  </p>
                </div>
              </div>
              <div style={{ flex: '1 1 260px' }}>
                <h3
                  style={{
                    fontSize: 'clamp(15px,1.8vw,16px)',
                    fontWeight: 700,
                    color: COL.pri,
                    margin: '0 0 12px',
                  }}
                >
                  Como funciona
                </h3>
                {[
                  {
                    t: 'API Key propia',
                    d: 'La empresa asigna su propia API Key de DeepSeek (u otro proveedor).',
                  },
                  {
                    t: 'Cache inteligente',
                    d: 'Preguntas repetitivas se almacenan y responden sin llamar a la IA.',
                  },
                  {
                    t: 'Saldo acumulable',
                    d: 'No vence. Lo que no consumes este mes se acumula para el siguiente.',
                  },
                  {
                    t: 'Tu controlas el gasto',
                    d: 'Agorax no obtiene ganancias. Solo pagas lo que consume tu bot.',
                  },
                ].map((x, i) => (
                  <div
                    key={i}
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '10px',
                      padding: '10px 0',
                      borderBottom: `1px solid ${COL.border}`,
                    }}
                  >
                    <FiCheck
                      size={16}
                      color={COL.green}
                      style={{ flexShrink: 0, marginTop: '2px' }}
                    />
                    <div>
                      <div style={{ fontSize: '14px', fontWeight: 600, color: COL.pri }}>{x.t}</div>
                      <div style={{ fontSize: '13px', color: COL.muted }}>{x.d}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ───── PLANES ───── */
function Planes() {
  const [ref, v] = useReveal();
  const nav = useNavigate();
  const [periodo, setPeriodo] = useState('mensual');
  const periodos = {
    mensual: { label: 'Mensual', mult: 1 },
    trimestral: { label: 'Trimestral', mult: 3 },
    semestral: { label: 'Semestral', mult: 6 },
    anual: { label: 'Anual', mult: 12 },
  };
  const p = periodos[periodo];
  const tipos = [
    {
      key: 'interno',
      titulo: 'Inventario Interno',
      desc: 'Gestión manual desde Agorax',
      planes: [
        { s: '1 Sede', p: 30 },
        { s: '2 Sedes', p: 55 },
        { s: '3 Sedes', p: 80 },
        { s: '4 Sedes', p: 100 },
        { s: '5 Sedes', p: 110 },
      ],
      descuentos: { mensual: 0, trimestral: 10, semestral: 20, anual: 40 },
      grad: `linear-gradient(135deg,${COL.pri} 0%,${COL.navy4} 100%)`,
    },
    {
      key: 'externo',
      titulo: 'Inventario Externo',
      desc: 'Sincronizado con tu sistema',
      planes: [
        { s: '1 Sede', p: 30 },
        { s: '2 Sedes', p: 55 },
        { s: '3 Sedes', p: 80 },
        { s: '4 Sedes', p: 100 },
        { s: '5 Sedes', p: 110 },
      ],
      descuentos: { mensual: 0, trimestral: 10, semestral: 20, anual: 40 },
      grad: `linear-gradient(135deg,${COL.pri} 0%,${COL.navy4} 100%)`,
    },
  ];
  const periodoLabel = p.mult === 1 ? '/mes' : periodo === 'anual' ? '/ano' : `/${p.mult} meses`;

  return (
    <section id="planes" style={{ ...SCT, background: COL.white }}>
      <style>{`@media(max-width:${BP}px){.planes-cols{grid-template-columns:1fr!important}}`}</style>
      <div style={M}>
        <div
          ref={ref}
          style={{
            opacity: v ? 1 : 0,
            transform: v ? 'translateY(0)' : 'translateY(30px)',
            transition: 'all 0.6s ease',
          }}
        >
          <h2
            style={{
              fontSize: 'clamp(24px,3.5vw,36px)',
              fontWeight: 800,
              color: COL.pri,
              textAlign: 'center',
              margin: '0 0 8px',
            }}
          >
            Planes y Convenio
          </h2>
          <p
            style={{
              fontSize: 'clamp(14px,1.8vw,16px)',
              color: COL.muted,
              textAlign: 'center',
              maxWidth: '600px',
              margin: '0 auto 32px',
              lineHeight: 1.6,
            }}
          >
            Precios por sede. Ambos tipos de inventario al mismo precio. Sin letra chica.
          </p>

          <div
            style={{
              background: `linear-gradient(135deg,${COL.greenBg},${COL.greenBg2})`,
              borderRadius: '16px',
              padding: 'clamp(20px,3vw,32px)',
              border: `2px solid ${COL.greenBorder}`,
              marginBottom: '32px',
            }}
          >
            <div
              style={{
                display: 'flex',
                gap: 'clamp(14px,2vw,20px)',
                alignItems: 'flex-start',
                flexWrap: 'wrap',
              }}
            >
              <div
                style={{
                  background: COL.green,
                  color: COL.white,
                  borderRadius: '50%',
                  width: '48px',
                  height: '48px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <FiCheck size={24} />
              </div>
              <div style={{ flex: 1 }}>
                <h3
                  style={{
                    fontSize: 'clamp(18px,2.2vw,22px)',
                    fontWeight: 800,
                    color: COL.greenDeep,
                    margin: '0 0 8px',
                  }}
                >
                  Convenio de Inicio
                </h3>
                <p
                  style={{
                    fontSize: 'clamp(14px,1.6vw,15px)',
                    color: COL.greenDeep2,
                    lineHeight: 1.7,
                    margin: '0 0 12px',
                  }}
                >
                  <strong>14 días de prueba gratuita</strong> con todas las configuraciones básicas
                  incluidas: alta de empresa, creación de sede, carga de catálogo inicial y
                  configuración del bot IA.
                </p>
                <div
                  style={{
                    background: COL.greenDeep,
                    borderRadius: '12px',
                    padding: 'clamp(16px,2vw,24px)',
                    color: COL.white,
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      gap: 'clamp(16px,3vw,32px)',
                      flexWrap: 'wrap',
                      justifyContent: 'center',
                      alignItems: 'center',
                    }}
                  >
                    <div style={{ textAlign: 'center', maxWidth: '300px' }}>
                      <div
                        style={{
                          fontSize: 'clamp(14px,1.5vw,15px)',
                          opacity: 0.7,
                          marginBottom: '4px',
                        }}
                      >
                        Al finalizar la prueba gratuita
                      </div>
                      <div
                        style={{
                          fontSize: 'clamp(22px,3vw,28px)',
                          fontWeight: 900,
                          lineHeight: 1.2,
                        }}
                      >
                        Configuración + 1ra mensualidad
                      </div>
                      <div
                        style={{
                          fontSize: 'clamp(11px,1.2vw,12px)',
                          opacity: 0.6,
                          marginTop: '4px',
                        }}
                      >
                        El costo por configuración se cobra junto con la primera mensualidad
                      </div>
                    </div>
                    <div
                      style={{
                        fontSize: 'clamp(24px,3vw,32px)',
                        opacity: 0.4,
                        display: window.innerWidth < BP ? 'none' : 'block',
                      }}
                    >
                      &#8594;
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <div
                        style={{
                          fontSize: 'clamp(14px,1.5vw,15px)',
                          opacity: 0.7,
                          marginBottom: '4px',
                        }}
                      >
                        Meses siguientes
                      </div>
                      <div style={{ fontSize: 'clamp(36px,5vw,48px)', fontWeight: 900 }}>$30</div>
                      <div
                        style={{
                          fontSize: 'clamp(11px,1.2vw,12px)',
                          opacity: 0.6,
                          marginTop: '4px',
                        }}
                      >
                        Por mes (1 sede)
                      </div>
                    </div>
                  </div>
                  <p
                    style={{
                      fontSize: 'clamp(12px,1.3vw,13px)',
                      opacity: 0.65,
                      textAlign: 'center',
                      margin: '12px 0 0',
                      lineHeight: 1.5,
                    }}
                  >
                    Al finalizar los 14 días de prueba gratuita se cobra el costo por configuración
                    junto con la primera mensualidad. Luego queda pagando la mensualidad regular
                    según la cantidad de sedes.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div
            style={{
              display: 'inline-flex',
              background: COL.neutral,
              borderRadius: '10px',
              padding: '3px',
              marginBottom: '32px',
              position: 'relative',
              left: '50%',
              transform: 'translateX(-50%)',
            }}
          >
            {Object.entries(periodos).map(([k, val]) => (
              <button
                key={k}
                onClick={() => setPeriodo(k)}
                style={{
                  padding: '8px 20px',
                  borderRadius: '8px',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '13px',
                  fontWeight: 600,
                  background: periodo === k ? COL.white : 'transparent',
                  color: periodo === k ? COL.pri2 : COL.muted,
                  transition: 'all 0.2s',
                  boxShadow: periodo === k ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                  minHeight: '40px',
                }}
              >
                {val.label}
              </button>
            ))}
          </div>

          <div
            className="planes-cols"
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 'clamp(16px,2.5vw,24px)',
            }}
          >
            {tipos.map((tipo) => (
              <div
                key={tipo.key}
                style={{
                  background: COL.bg,
                  borderRadius: '16px',
                  border: `1px solid ${COL.border}`,
                  overflow: 'hidden',
                  boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
                }}
              >
                <div
                  style={{
                    background: tipo.grad,
                    padding: 'clamp(16px,2vw,24px) clamp(18px,2.5vw,28px)',
                  }}
                >
                  <div
                    style={{ fontSize: 'clamp(16px,2vw,20px)', fontWeight: 800, color: COL.white }}
                  >
                    {tipo.titulo}
                  </div>
                  <div
                    style={{
                      fontSize: 'clamp(12px,1.4vw,14px)',
                      color: 'rgba(255,255,255,0.65)',
                      marginTop: '4px',
                    }}
                  >
                    {tipo.desc}
                  </div>
                </div>
                <div style={{ padding: 'clamp(14px,2vw,20px) clamp(18px,2.5vw,28px)' }}>
                  {tipo.planes.map((pl, i) => {
                    const total = pl.p * p.mult - tipo.descuentos[periodo];
                    return (
                      <div
                        key={i}
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          padding: 'clamp(10px,1.5vw,14px) 0',
                          borderBottom: i < 4 ? `1px solid ${COL.light}` : 'none',
                        }}
                      >
                        <span
                          style={{
                            fontSize: 'clamp(13px,1.5vw,15px)',
                            color: COL.text,
                            fontWeight: 500,
                          }}
                        >
                          {pl.s}
                        </span>
                        <div style={{ textAlign: 'right' }}>
                          <span
                            style={{
                              fontSize: 'clamp(20px,2.5vw,26px)',
                              fontWeight: 800,
                              color: COL.pri,
                            }}
                          >
                            ${total}
                          </span>
                          <span
                            style={{
                              fontSize: 'clamp(11px,1.2vw,12px)',
                              color: COL.muted,
                              marginLeft: '4px',
                            }}
                          >
                            {periodoLabel}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                  <div
                    style={{
                      padding: 'clamp(10px,1.5vw,14px) 0 0',
                      fontSize: 'clamp(12px,1.3vw,13px)',
                      color: COL.acc,
                      fontWeight: 600,
                      fontStyle: 'italic',
                    }}
                  >
                    +$10 por cada sede adicional
                  </div>
                  {tipo.descuentos[periodo] > 0 && (
                    <div
                      style={{
                        background: COL.greenBg,
                        borderRadius: '8px',
                        padding: '8px 12px',
                        textAlign: 'center',
                        marginTop: '12px',
                      }}
                    >
                      <span style={{ fontSize: '13px', fontWeight: 700, color: COL.green }}>
                        Ahorras ${tipo.descuentos[periodo]}
                      </span>
                    </div>
                  )}
                  <ul style={{ listStyle: 'none', padding: '0', margin: '16px 0 0' }}>
                    {[
                      'Bot IA configurable',
                      '9 métodos de pago',
                      'POS multi-moneda',
                      'Asistencia QR',
                      'Panel admin y roles',
                      'Sincronización externa',
                      'Soporte incluido',
                    ].map((f, j) => (
                      <li
                        key={j}
                        style={{
                          fontSize: '12px',
                          color: COL.muted,
                          padding: '5px 0',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                        }}
                      >
                        <FiCheck size={12} color={COL.green} /> {f}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
          <div
            style={{
              textAlign: 'center',
              marginTop: '24px',
              display: 'flex',
              flexDirection: 'column',
              gap: '12px',
              alignItems: 'center',
            }}
          >
            <button
              onClick={() => nav('/registro')}
              style={{
                background: `linear-gradient(135deg,${COL.green},${COL.greenDark})`,
                color: COL.white,
                border: 'none',
                padding: '14px 32px',
                borderRadius: '12px',
                fontWeight: 700,
                fontSize: 'clamp(14px,1.6vw,15px)',
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                minHeight: '48px',
                boxShadow: `0 4px 20px ${COL.green}30`,
              }}
            >
              Empieza Gratis — 14 días <FiArrowRight size={16} />
            </button>
            <button
              onClick={() => nav('/detalle/precios')}
              style={{
                background: 'transparent',
                color: COL.acc,
                border: `2px solid ${COL.acc}`,
                padding: '10px 24px',
                borderRadius: '10px',
                fontWeight: 600,
                fontSize: 'clamp(13px,1.4vw,14px)',
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                minHeight: '44px',
              }}
            >
              Ver precios y costos IA <FiArrowRight size={14} />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ───── CREDITO ───── */
function Credito() {
  const [ref, v] = useReveal();
  return (
    <section
      id="credito"
      style={{
        ...SCT,
        background: `linear-gradient(160deg, ${COL.pri} 0%, ${COL.navy1} 30%, ${COL.navy2} 60%, ${COL.navy3} 100%)`,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background:
            'radial-gradient(ellipse 60% 50% at 70% 40%, rgba(139,92,246,0.08), transparent 70%)',
        }}
      />
      <div style={M}>
        <div
          ref={ref}
          style={{
            opacity: v ? 1 : 0,
            transform: v ? 'translateY(0)' : 'translateY(30px)',
            transition: 'all 0.6s ease',
            position: 'relative',
            zIndex: 2,
          }}
        >
          <div style={{ textAlign: 'center', marginBottom: 'clamp(32px,5vw,48px)' }}>
            <div
              style={{
                display: 'inline-block',
                background: 'rgba(139,92,246,0.15)',
                border: '1px solid rgba(139,92,246,0.3)',
                padding: '6px 16px',
                borderRadius: '20px',
                fontSize: 'clamp(12px,1.4vw,13px)',
                fontWeight: 600,
                color: COL.purple,
                letterSpacing: '0.5px',
                marginBottom: '20px',
              }}
            >
              Módulo Activo
            </div>
            <h2
              style={{
                fontSize: 'clamp(28px,4vw,42px)',
                fontWeight: 900,
                color: COL.white,
                margin: '0 0 12px',
              }}
            >
              Sistema de Crédito y Cuentas por Cobrar
            </h2>
            <p
              style={{
                fontSize: 'clamp(14px,1.8vw,16px)',
                color: 'rgba(255,255,255,0.55)',
                maxWidth: '620px',
                margin: '0 auto',
                lineHeight: 1.7,
              }}
            >
              Gestiona el crédito de todos tus clientes con cuotas, plazos definidos y control
              total de cuentas por cobrar integrado directamente en Agorax.
            </p>
          </div>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%,280px), 1fr))',
              gap: 'clamp(14px,2vw,20px)',
              marginBottom: 'clamp(24px,4vw,36px)',
            }}
          >
            {[
              {
                icon: <FiUsers size={22} />,
                t: 'Clientes con Crédito',
                d: 'Asigna límites de crédito personalizados a cada cliente. Define montos máximos, plazos de pago y condiciones específicas por cliente. Todo desde su perfil.',
              },
              {
                icon: <FiClock size={22} />,
                t: 'Cuotas por Lapso de Tiempo',
                d: 'Configura planes de pago en cuotas semanales, quincenales o mensuales. El sistema calcula automáticamente las fechas de vencimiento de cada cuota.',
              },
              {
                icon: <FiDollarSign size={22} />,
                t: 'Seguimiento de Saldos',
                d: 'Panel visual con el estado de cada cuenta: saldo pendiente, cuotas pagadas, cuotas vencidas, proximas a vencer. Alertas automáticas de morosidad.',
              },
              {
                icon: <FiBarChart2 size={22} />,
                t: 'Reportes de Cartera',
                d: 'Reportes consolidados de cuentas por cobrar: antiguedad de saldos, indice de morosidad, proyeccion de ingresos, ranking de clientes por consumo.',
              },
              {
                icon: <FiCreditCard size={22} />,
                t: 'Abonos y Pagos Parciales',
                d: 'Registra abonos parciales a las cuentas. El sistema recalcula automáticamente el saldo restante y ajusta las proximas cuotas si es necesario.',
              },
              {
                icon: <FiZap size={22} />,
                t: 'Integración Total',
                d: 'El crédito se integra con pedidos, POS y el perfil del cliente. Un cliente puede comprar a crédito desde el chat con el bot o directamente en el punto de venta.',
              },
            ].map((x, i) => (
              <div
                key={i}
                style={{
                  background: 'rgba(255,255,255,0.05)',
                  borderRadius: '14px',
                  padding: 'clamp(18px,2.5vw,28px)',
                  border: '1px solid rgba(255,255,255,0.08)',
                  backdropFilter: 'blur(10px)',
                }}
              >
                <div
                  style={{
                    width: '48px',
                    height: '48px',
                    borderRadius: '14px',
                    background: 'rgba(139,92,246,0.2)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: COL.purple,
                    marginBottom: '16px',
                  }}
                >
                  {x.icon}
                </div>
                <h3
                  style={{
                    fontSize: 'clamp(16px,1.8vw,18px)',
                    fontWeight: 700,
                    color: COL.white,
                    margin: '0 0 8px',
                  }}
                >
                  {x.t}
                </h3>
                <p
                  style={{
                    fontSize: 'clamp(13px,1.4vw,14px)',
                    color: 'rgba(255,255,255,0.55)',
                    lineHeight: 1.6,
                    margin: 0,
                  }}
                >
                  {x.d}
                </p>
              </div>
            ))}
          </div>

          <div
            style={{
              background: 'rgba(139,92,246,0.1)',
              borderRadius: '16px',
              padding: 'clamp(20px,3vw,32px)',
              border: '1px solid rgba(139,92,246,0.2)',
              textAlign: 'center',
            }}
          >
            <h3
              style={{
                fontSize: 'clamp(18px,2.2vw,22px)',
                fontWeight: 800,
                color: COL.white,
                margin: '0 0 12px',
              }}
            >
              Lleva Todo el Control de tus Cuentas por Cobrar
            </h3>
            <p
              style={{
                fontSize: 'clamp(14px,1.6vw,15px)',
                color: 'rgba(255,255,255,0.55)',
                maxWidth: '700px',
                margin: '0 auto',
                lineHeight: 1.7,
              }}
            >
              Sabemos que el crédito es el corazón de muchos negocios. Por eso este módulo está
              diseñado para que nunca pierdas el control:
              <strong style={{ color: COL.purple }}>
                {' '}
                quién te debe, cuánto, desde cuándo y cuándo vence cada cuota
              </strong>
              . Adiós a las libretas y hojas de cálculo. Todo tu sistema de cuentas por cobrar en el
              mismo lugar donde gestionas tus ventas, inventario y pedidos.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ───── ROADMAP ───── */
function Roadmap() {
  const [ref, v] = useReveal();
  const nav = useNavigate();
  const previews = [
    {
      icon: FiUsers,
      color: COL.purple,
      t: 'Rol Empresario',
      desc: 'Panel multi-empresa con reportes consolidados y métricas comparativas de todos tus negocios en un solo lugar.',
      destacado: true,
    },
    {
      icon: FiUsers,
      color: COL.purple,
      t: 'Roles Especializados',
      desc: 'Carnicero y Depositario: roles para rubros específicos con control de pesables y stock físico.',
    },
    {
      icon: FiZap,
      color: COL.amber,
      t: 'Nuevas Funcionalidades',
      desc: 'GPS en vivo, ETA inteligente y dashboard avanzado con gráficos interactivos.',
    },
    {
      icon: FiCpu,
      color: COL.teal,
      t: 'Mejoras en IA',
      desc: 'Autoaprendizaje continuo, entrenamiento personalizado por empresa y voz/audio (Whisper).',
    },
  ];
  return (
    <section id="roadmap" style={{ ...SCT, background: COL.bg }}>
      <div style={M}>
        <div
          ref={ref}
          style={{
            opacity: v ? 1 : 0,
            transform: v ? 'translateY(0)' : 'translateY(30px)',
            transition: 'all 0.6s ease',
          }}
        >
          <h2
            style={{
              fontSize: 'clamp(24px,3.5vw,36px)',
              fontWeight: 800,
              color: COL.pri,
              textAlign: 'center',
              margin: '0 0 8px',
            }}
          >
            Próximos Avances
          </h2>
          <p
            style={{
              fontSize: 'clamp(14px,1.8vw,16px)',
              color: COL.muted,
              textAlign: 'center',
              maxWidth: '560px',
              margin: '0 auto 40px',
              lineHeight: 1.6,
            }}
          >
            Agorax no es un producto estático. Evoluciona constantemente para adaptarse a cualquier
            tipo de negocio.
          </p>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%,280px), 1fr))',
              gap: 'clamp(14px,2vw,20px)',
            }}
          >
            {previews.map((pr, i) => (
              <div
                key={i}
                style={{
                  background: pr.destacado
                    ? `linear-gradient(135deg,${COL.purple}10,${COL.purpleBg})`
                    : COL.bg,
                  borderRadius: '14px',
                  padding: 'clamp(16px,2vw,24px)',
                  border: pr.destacado ? `2px solid ${COL.purple}40` : `1px solid ${COL.border}`,
                  textAlign: 'center',
                  position: 'relative',
                  overflow: pr.destacado ? 'visible' : 'hidden',
                }}
              >
                {pr.destacado && (
                  <div
                    style={{
                      position: 'absolute',
                      top: '-10px',
                      left: '50%',
                      transform: 'translateX(-50%)',
                      background: COL.purple,
                      color: COL.white,
                      padding: '3px 14px',
                      borderRadius: '10px',
                      fontSize: '11px',
                      fontWeight: 700,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    Proxima prioridad
                  </div>
                )}
                <div
                  style={{
                    width: '44px',
                    height: '44px',
                    borderRadius: '12px',
                    background: `${pr.color}15`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: pr.color,
                    margin: '0 auto 14px',
                    marginTop: pr.destacado ? '6px' : '0',
                  }}
                >
                  <pr.icon size={20} />
                </div>
                <h3
                  style={{
                    fontSize: 'clamp(15px,1.7vw,16px)',
                    fontWeight: 700,
                    color: pr.destacado ? COL.purple : COL.pri,
                    margin: '0 0 8px',
                  }}
                >
                  {pr.t}
                </h3>
                <p
                  style={{ fontSize: 'clamp(12px,1.3vw,13px)', color: COL.muted, lineHeight: 1.5 }}
                >
                  {pr.desc}
                </p>
              </div>
            ))}
          </div>

          <div style={{ textAlign: 'center', marginTop: '24px' }}>
            <button
              onClick={() => nav('/detalle/roadmap')}
              style={{
                background: 'transparent',
                color: COL.acc,
                border: `2px solid ${COL.acc}`,
                padding: '10px 24px',
                borderRadius: '10px',
                fontWeight: 600,
                fontSize: 'clamp(13px,1.4vw,14px)',
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                minHeight: '44px',
              }}
            >
              Ver detalle completo <FiArrowRight size={14} />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ───── CTA ───── */
function CTA() {
  const [ref, v] = useReveal();
  const nav = useNavigate();
  return (
    <section
      style={{
        ...SCT,
        background: `linear-gradient(135deg,${COL.pri} 0%,${COL.navy4} 50%,${COL.navy3} 100%)`,
      }}
    >
      <div style={M}>
        <div
          ref={ref}
          style={{
            textAlign: 'center',
            opacity: v ? 1 : 0,
            transform: v ? 'translateY(0)' : 'translateY(30px)',
            transition: 'all 0.6s ease',
          }}
        >
          <h2
            style={{
              fontSize: 'clamp(24px,3.5vw,36px)',
              fontWeight: 800,
              color: COL.white,
              margin: '0 0 16px',
            }}
          >
            Lleva tu Negocio al Siguiente Nivel
          </h2>
          <p
            style={{
              fontSize: 'clamp(14px,1.8vw,16px)',
              color: 'rgba(255,255,255,0.55)',
              maxWidth: '500px',
              margin: '0 auto 32px',
              lineHeight: 1.6,
            }}
          >
            Automatiza tus ventas 24/7 con inteligencia artificial. Gestiona pedidos, inventario,
            POS, asistencia y deliverys desde una sola plataforma.
          </p>
          <div style={{ display: 'flex', gap: '14px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <button
              onClick={() => nav('/registro')}
              style={{
                background: `linear-gradient(135deg,${COL.green},${COL.greenDark})`,
                color: COL.white,
                border: 'none',
                borderRadius: '12px',
                padding: 'clamp(14px,2vw,16px) clamp(28px,4vw,36px)',
                fontWeight: 700,
                fontSize: 'clamp(14px,1.6vw,15px)',
                cursor: 'pointer',
                boxShadow: `0 4px 24px ${COL.green}40`,
                display: 'inline-flex',
                alignItems: 'center',
                gap: '10px',
                minHeight: '48px',
              }}
            >
              <FiArrowRight size={16} /> Registrate Gratis
            </button>
          </div>
          <div
            style={{
              marginTop: '48px',
              display: 'flex',
              justifyContent: 'center',
              gap: 'clamp(20px,4vw,48px)',
              flexWrap: 'wrap',
            }}
          >
            {[
              { l: 'Teléfono', v: '+58 412 228.40.30' },
              { l: 'Email', v: 'johannda66@gmail.com' },
              { l: 'Ubicación', v: 'Venezuela' },
            ].map((c) => (
              <div key={c.l} style={{ textAlign: 'center' }}>
                <div
                  style={{ fontSize: '12px', color: 'rgba(255,255,255,0.4)', marginBottom: '4px' }}
                >
                  {c.l}
                </div>
                <div style={{ fontSize: '14px', color: 'rgba(255,255,255,0.65)', fontWeight: 500 }}>
                  {c.v}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ───── NAV ───── */
function Navbar({ stuck, menuOpen, setMenuOpen, activeSection, onAbrirResumen }) {
  const nav = useNavigate();
  const [openGroup, setOpenGroup] = useState(null);
  const goTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
    setOpenGroup(null);
    setMenuOpen(false);
  };
  return (
    <nav
      style={{
        padding: '14px 0',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          cursor: 'pointer',
          marginRight: '48px',
        }}
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
      >
        <div
          style={{
            width: '36px',
            height: '36px',
            borderRadius: '10px',
            backgroundColor: COL.white,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <img
            src="/logo-agorax.jpeg"
            alt="AGORAX"
            style={{ width: '28px', height: '28px', borderRadius: '6px', objectFit: 'contain' }}
          />
        </div>
        <span style={{ fontWeight: 800, fontSize: '20px', color: COL.white, letterSpacing: '-0.5px' }}>
          AGORAX
        </span>
      </div>
      <div style={{ display: 'flex', gap: '28px', alignItems: 'center' }}>
        <div
          style={{
            display: window.innerWidth < BP ? 'none' : 'flex',
            gap: '4px',
            alignItems: 'center',
          }}
        >
          {NAV_GROUPS.map((g) => {
            const groupActive = g.items.some((s) => s.id === activeSection);
            return (
              <div
                key={g.label}
                style={{ position: 'relative' }}
                onMouseEnter={() => setOpenGroup(g.label)}
                onMouseLeave={() => setOpenGroup(null)}
              >
                <button
                  onClick={() => setOpenGroup(openGroup === g.label ? null : g.label)}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: groupActive ? COL.white : 'rgba(255,255,255,0.55)',
                    fontSize: '13px',
                    fontWeight: groupActive ? 600 : 400,
                    cursor: 'pointer',
                    padding: '8px 12px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    transition: 'color 0.2s',
                  }}
                >
                  {g.label}
                  <span style={{ fontSize: '10px', opacity: 0.6 }}>&#9662;</span>
                </button>
                {openGroup === g.label && (
                  <div
                    style={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      minWidth: '200px',
                      background: 'rgba(10,14,39,0.97)',
                      backdropFilter: 'blur(14px)',
                      borderRadius: '12px',
                      border: '1px solid rgba(255,255,255,0.1)',
                      padding: '8px',
                      boxShadow: '0 12px 32px rgba(0,0,0,0.3)',
                      zIndex: 1001,
                    }}
                  >
                    {g.items.map((s) => (
                      <button
                        key={s.id}
                        onClick={() => goTo(s.id)}
                        style={{
                          display: 'block',
                          width: '100%',
                          textAlign: 'left',
                          background: s.id === activeSection ? 'rgba(99,130,235,0.15)' : 'transparent',
                          border: 'none',
                          color: s.id === activeSection ? COL.white : 'rgba(255,255,255,0.7)',
                          fontSize: '13px',
                          fontWeight: s.id === activeSection ? 600 : 400,
                          cursor: 'pointer',
                          padding: '9px 12px',
                          borderRadius: '8px',
                          transition: 'background 0.15s',
                        }}
                      >
                        {s.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        <button
          onClick={onAbrirResumen}
          style={{
            background: 'transparent',
            color: COL.amber,
            fontSize: '13px',
            fontWeight: 600,
            border: '1px solid rgba(245,158,11,0.4)',
            borderRadius: '8px',
            padding: '8px 14px',
            cursor: 'pointer',
            display: window.innerWidth < BP ? 'none' : 'inline-flex',
            minHeight: '38px',
            alignItems: 'center',
            gap: '6px',
            transition: 'background 0.2s',
          }}
          onMouseOver={(e) => (e.currentTarget.style.background = 'rgba(245,158,11,0.15)')}
          onMouseOut={(e) => (e.currentTarget.style.background = 'transparent')}
        >
          <FiPlay size={14} /> Resumen
        </button>
        <a
          href={getAdminUrl()}
          target="_blank"
          rel="noreferrer"
          style={{
            background: 'rgba(255,255,255,0.12)',
            color: COL.white,
            fontSize: '13px',
            fontWeight: 600,
            textDecoration: 'none',
            cursor: 'pointer',
            borderRadius: '8px',
            padding: '8px 16px',
            transition: 'background 0.2s',
            display: window.innerWidth < BP ? 'none' : 'inline-flex',
            minHeight: '38px',
            alignItems: 'center',
          }}
          onMouseOver={(e) => (e.currentTarget.style.background = 'rgba(255,255,255,0.2)')}
          onMouseOut={(e) => (e.currentTarget.style.background = 'rgba(255,255,255,0.12)')}
        >
          Acceder
        </a>
        <button
          onClick={() => nav('/registro')}
          style={{
            background: COL.green,
            color: COL.white,
            border: 'none',
            borderRadius: '8px',
            padding: '8px 16px',
            fontWeight: 600,
            fontSize: '13px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            minHeight: '38px',
          }}
        >
          Registrate
        </button>
        <button
          onClick={() => setMenuOpen(!menuOpen)}
          style={{
            display: window.innerWidth < BP ? 'block' : 'none',
            background: 'none',
            border: 'none',
            color: COL.white,
            fontSize: '22px',
            cursor: 'pointer',
            padding: '8px',
            minWidth: '44px',
            minHeight: '44px',
          }}
        >
          {menuOpen ? <FiX /> : <FiMenu />}
        </button>
      </div>
    </nav>
  );
}

/* ───── FOOTER ───── */
function Footer() {
  const nav = useNavigate();
  return (
    <footer style={{ background: COL.pri, padding: '32px 0' }}>
      <div style={M}>
        <div
          style={{
            display: 'flex',
            gap: 'clamp(20px,4vw,48px)',
            flexWrap: 'wrap',
            justifyContent: 'space-between',
          }}
        >
          <div style={{ minWidth: '180px' }}>
            <div
              style={{ fontWeight: 800, fontSize: '20px', color: COL.white, marginBottom: '12px' }}
            >
              AGORAX
            </div>
            <p
              style={{
                fontSize: '13px',
                color: 'rgba(255,255,255,0.45)',
                lineHeight: 1.6,
                maxWidth: '260px',
              }}
            >
              Plataforma de gestión empresarial con inteligencia artificial. Automatiza, escala y
              crece.
            </p>
          </div>
          <div style={{ minWidth: '140px' }}>
            <div
              style={{
                fontWeight: 700,
                fontSize: '13px',
                color: 'rgba(255,255,255,0.6)',
                marginBottom: '14px',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
              }}
            >
              Módulos
            </div>
            {[
              { l: 'Bot IA', to: '/detalle/bot-ia' },
              { l: 'Pedidos y Pagos', to: '/detalle/pedidos' },
              { l: 'Restaurante', sec: 'restaurante' },
              { l: 'Inventario', to: '/detalle/inventario' },
              { l: 'Back-office', sec: 'operaciones' },
              { l: 'Vendedores', sec: 'vendedores' },
              { l: 'Reportes', sec: 'reportes' },
              { l: 'Permisos y Roles', sec: 'permisos' },
              { l: 'Asistencia QR', sec: 'asistencia' },
            ].map((lk, i) => (
              <div
                key={i}
                onClick={() =>
                  lk.to ? nav(lk.to) : document.getElementById(lk.sec)?.scrollIntoView({ behavior: 'smooth' })
                }
                style={{
                  fontSize: '13px',
                  color: 'rgba(255,255,255,0.45)',
                  padding: '5px 0',
                  cursor: 'pointer',
                  transition: 'color 0.2s',
                }}
                onMouseOver={(e) => (e.currentTarget.style.color = COL.white)}
                onMouseOut={(e) => (e.currentTarget.style.color = 'rgba(255,255,255,0.45)')}
              >
                {lk.l}
              </div>
            ))}
          </div>
          <div style={{ minWidth: '140px' }}>
            <div
              style={{
                fontWeight: 700,
                fontSize: '13px',
                color: 'rgba(255,255,255,0.6)',
                marginBottom: '14px',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
              }}
            >
              Más
            </div>
            {[
              { l: 'Roadmap', to: '/detalle/roadmap' },
              { l: 'Precios', to: '/detalle/precios' },
              { l: 'Registro', to: '/registro' },
              {
                l: 'Acceder',
                to: null,
                onClick: () => window.open(getAdminUrl(), '_blank'),
              },
              {
                l: 'Contacto',
                to: null,
                onClick: () =>
                  document.getElementById('cta')?.scrollIntoView({ behavior: 'smooth' }),
              },
            ].map((lk, i) => (
              <div
                key={i}
                onClick={() => (lk.to ? nav(lk.to) : lk.onClick?.())}
                style={{
                  fontSize: '13px',
                  color: 'rgba(255,255,255,0.45)',
                  padding: '5px 0',
                  cursor: 'pointer',
                  transition: 'color 0.2s',
                }}
                onMouseOver={(e) => (e.currentTarget.style.color = COL.white)}
                onMouseOut={(e) => (e.currentTarget.style.color = 'rgba(255,255,255,0.45)')}
              >
                {lk.l}
              </div>
            ))}
          </div>
        </div>
        <div
          style={{
            borderTop: '1px solid rgba(255,255,255,0.06)',
            marginTop: '32px',
            paddingTop: '20px',
            textAlign: 'center',
            fontSize: '12px',
            color: 'rgba(255,255,255,0.3)',
          }}
        >
          &copy; {new Date().getFullYear()} Agorax. Todos los derechos reservados.
        </div>
      </div>
    </footer>
  );
}

/* ───── MOBILE MENU ───── */
function MobileMenu({ open, onClose, activeSection }) {
  const nav = useNavigate();
  if (!open) return null;
  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        top: '60px',
        zIndex: 999,
        background: 'rgba(10,14,39,0.97)',
        backdropFilter: 'blur(20px)',
        display: 'flex',
        flexDirection: 'column',
        padding: '40px 24px',
        gap: '4px',
      }}
    >
      {NAV_GROUPS.map((g) => (
        <div key={g.label} style={{ marginBottom: '8px' }}>
          <div
            style={{
              fontSize: '11px',
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              color: 'rgba(255,255,255,0.35)',
              padding: '8px 16px 4px',
            }}
          >
            {g.label}
          </div>
          {g.items.map((s) => (
            <button
              key={s.id}
              onClick={() => {
                const el = document.getElementById(s.id);
                if (el) el.scrollIntoView({ behavior: 'smooth' });
                onClose();
              }}
              style={{
                background: s.id === activeSection ? 'rgba(99,130,235,0.12)' : 'transparent',
                border: 'none',
                color: s.id === activeSection ? COL.white : 'rgba(255,255,255,0.55)',
                fontSize: '16px',
                fontWeight: s.id === activeSection ? 600 : 400,
                cursor: 'pointer',
                padding: '12px 16px',
                borderRadius: '10px',
                textAlign: 'left',
                minHeight: '44px',
                width: '100%',
              }}
            >
              {s.label}
            </button>
          ))}
        </div>
      ))}
      <div
        style={{
          marginTop: '16px',
          padding: '0 16px',
          display: 'flex',
          flexDirection: 'column',
          gap: '10px',
        }}
      >
        <button
          onClick={() => {
            onClose();
            nav('/registro');
          }}
          style={{
            width: '100%',
            background: `linear-gradient(135deg,${COL.green},${COL.greenDark})`,
            color: COL.white,
            border: 'none',
            borderRadius: '10px',
            padding: '14px',
            fontWeight: 700,
            fontSize: '15px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            minHeight: '48px',
          }}
        >
          Registrate Gratis
        </button>
        <a
          href={getAdminUrl()}
          target="_blank"
          rel="noreferrer"
          style={{
            width: '100%',
            background: 'rgba(255,255,255,0.08)',
            color: COL.white,
            border: '1px solid rgba(255,255,255,0.15)',
            borderRadius: '10px',
            padding: '14px',
            fontWeight: 600,
            fontSize: '15px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            minHeight: '48px',
            textDecoration: 'none',
            boxSizing: 'border-box',
          }}
        >
          Acceder
        </a>
      </div>
    </div>
  );
}

/* ───── MAIN ───── */
function LandingPage() {
  const [stuck, setStuck] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [tourOpen, setTourOpen] = useState(false);
  const activeSection = useScrollSpy();
  const nav = useNavigate();

  useEffect(() => {
    const onScroll = () => setStuck(window.scrollY > 60);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const hash = window.location.hash;
    if (hash) {
      setTimeout(() => {
        const el = document.querySelector(hash);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    }
  }, []);

  return (
    <div
      style={{
        background: COL.bg,
        fontFamily: "'Inter','Segoe UI',system-ui,-apple-system,sans-serif",
        color: COL.text,
        WebkitFontSmoothing: 'antialiased',
      }}
    >
      <style>{`@keyframes bounce{0%,100%{transform:translateY(0)}50%{transform:translateY(10px)}}*{box-sizing:border-box}html{scroll-behavior:smooth}body{margin:0;overflow:auto!important}.flip-card{border-radius:14px}.flip-card:focus-visible{outline:2px solid ${COL.acc};outline-offset:4px}`}</style>
      <div style={stickyStyle(stuck, menuOpen)}>
        <div style={M}>
          <Navbar
            stuck={stuck}
            menuOpen={menuOpen}
            setMenuOpen={setMenuOpen}
            activeSection={activeSection}
            onAbrirResumen={() => setTourOpen(true)}
          />
        </div>
      </div>
      <MobileMenu
        open={menuOpen}
        onClose={() => setMenuOpen(false)}
        activeSection={activeSection}
      />
      <PresentacionRapida
        open={tourOpen}
        onClose={() => setTourOpen(false)}
        onCta={() => nav('/registro')}
      />
      <Hero onAbrirResumen={() => setTourOpen(true)} />
      <Piramide />
      <ProblemaSolucion />
      <AreaPublica />
      <BotIA />
      <Pedidos />
      <PuntoDeVenta />
      <Restaurante />
      <Delivery />
      <Inventario />
      <Operaciones />
      <Vendedores />
      <Reportes />
      <PermisosRoles />
      <Asistencia />
      <CostoIA />
      <Planes />
      <Credito />
      <Roadmap />
      <section id="cta">
        <CTA />
      </section>
      <Footer />
    </div>
  );
}

export default LandingPage;
